export interface ProductChangelogEntry {
  readonly version: string;
  readonly date: string;
  readonly title: string;
  readonly changes: readonly string[];
}

/**
 * Changelog utilisateur de Tosho.
 *
 * Cette liste est volontairement vulgarisée : elle décrit ce qui change pour l'utilisateur sans
 * exposer l'architecture interne, les mécanismes de sécurité détaillés ni les procédures de
 * déploiement. Le premier élément doit toujours correspondre à la version actuellement publiée ;
 * le test associé rend donc l'oubli de mise à jour visible lors de la CI.
 */
export const TOSHO_PRODUCT_CHANGELOG: readonly ProductChangelogEntry[] = [
  {
    version: 'D00.06.04#443',
    date: '3 novembre 2026',
    title: 'Sites et serveurs plus lisibles sur smartphone',
    changes: [
      'Sur smartphone, Client, CMS et serveur sont regroupés sur une même ligne. Les logos remplacent les noms, affichés seulement lorsqu’un logo est absent.',
      'Les sauvegardes, le stockage et les informations du vhost occupent chacun toute la largeur. Les détails du vhost peuvent être dépliés.',
      'Les fiches serveurs sont compactes, avec des informations techniques en lignes et des actions accessibles. La présentation desktop est conservée.',
    ],
  },
  {
    version: 'D00.06.04#440',
    date: '3 novembre 2026',
    title: 'Fermeture des fenêtres depuis le voile noir',
    changes: [
      'La croix blanche est trois fois plus grande, en haut à droite de l’écran sur le voile noir, toujours hors du contenu. Elle reste sans cadre au survol et au clavier, sur ordinateur et mobile.',
      'Le bandeau des fiches serveur remplit la largeur de la fenêtre, comme celui des fiches vhost.',
      'Si l’actualisation des alertes échoue après un relevé vide, son message reste visible sans créer de faux incident serveur.',
    ],
  },
  {
    version: 'D00.06.04#433',
    date: '3 novembre 2026',
    title: 'Copies Google Drive conservées malgré une entrée à vérifier',
    changes: [
      'Une entrée dont Plesk ne reconnaît pas le résultat ne masque plus les autres copies Google Drive signalées comme réussies. Les cinq dernières copies confirmées restent affichées.',
      'Une liste entièrement indéterminée ne signifie jamais qu’aucune sauvegarde n’existe. Une programmation absente concerne l’abonnement affiché, sans nier les tâches globales du serveur.',
    ],
  },
  {
    version: 'D00.06.04#432',
    date: '3 novembre 2026',
    title: 'Cockpit et ses instructions d’accès',
    changes: [
      'Cockpit rejoint la liste des panneaux de contrôle des serveurs avec son logo.',
      'Les réglages Cockpit permettent d’enregistrer l’URL du panneau et un commentaire libre pour les instructions d’accès.',
      'Ouvrez ensuite le panneau depuis le lien enregistré, après avoir ouvert votre tunnel SSH si nécessaire.',
    ],
  },
  {
    version: 'D00.06.04#431',
    date: '3 novembre 2026',
    title: 'Suivi Google Drive et fenêtres cohérentes',
    changes: [
      'Le suivi des sauvegardes se concentre sur Google Drive : dernière copie, présence vérifiée et programmation Plesk lorsqu’elle peut être lue. Les cinq dernières copies indiquent leur type complète ou incrémentale, avec des tailles en Mo et Go.',
      'Les erreurs de lecture ne sont plus présentées comme des incidents de sauvegarde sur l’accueil. Les mises à jour Plesk qui nécessitent une installation manuelle indiquent l’action à effectuer.',
      'Les fenêtres partagent la même croix blanche sans cadre, ancrée dans leur bandeau. La rubrique courante est conservée après un rechargement du navigateur.',
      'La liste des Clients des filtres se complète automatiquement et les deux lignes de filtres sont rapprochées. Les mentions techniques inutiles de l’accueil et du registre des serveurs sont retirées.',
    ],
  },
  {
    version: 'D00.06.04#428',
    date: '2 novembre 2026',
    title: 'Copies locales Plesk visibles malgré un état non précisé',
    changes: [
      'Une sauvegarde dont Plesk ne précise pas l’état reste présente dans la liste, avec sa date et sa taille disponibles.',
      'Ces copies portent le libellé État non précisé par Plesk et ne sont comptées ni comme un échec ni comme une copie réussie.',
    ],
  },
  {
    version: 'D00.06.04#427',
    date: '2 novembre 2026',
    title: 'Catalogues Plesk et filtres des sites',
    changes: [
      'Le catalogue distant retourné par Plesk apparaît dans la liste des sites, même sans lecture Google Drive directe.',
      'Les archives reprennent leur date Plesk, leur type et leur taille quand elle est retournée ; la portée de l’abonnement reste indiquée.',
      'Filtrez les domaines, les sous-domaines et les environnements avant pagination, avec des compteurs cohérents.',
      'Les sites inactifs portent le libellé Site désactivé ; leurs actions restent visibles et utilisables.',
    ],
  },
  {
    version: 'D00.06.04#424',
    date: '2 novembre 2026',
    title: 'Sauvegardes, recherche et listes plus lisibles',
    changes: [
      'Les relevés de sauvegardes locales et distantes sont suivis séparément ; une erreur conserve les derniers résultats disponibles.',
      'Les sites suspendus ou désactivés sont identifiés et grisés, avec des boutons toujours visibles et utilisables.',
      'Client et CMS partagent les mêmes alignements ; Matomo Analytics rejoint les choix disponibles avec son logo.',
      'Les sous-domaines suivent leur domaine principal. La date de création Plesk préremplit la fiche et votre date manuelle reste prioritaire.',
      'Votre choix du nombre de lignes est retenu dans ce navigateur après navigation, rechargement et reconnexion.',
      'La recherche suggère les rubriques, Clients, prestataires, serveurs et sites accessibles, avec ouverture directe et navigation au clavier.',
      'Les alertes Plesk défilent dans un carrousel avec les logos des propriétaires ; projets et événements passent avant ces alertes.',
      'Les fenêtres partagent une croix blanche et les messages de résultat un fond sombre avec succès vert ou échec rouge rosé.',
      'La version apparaît sous le logo Plesk. Les libellés, dates et actions Freebe sont clarifiés.',
    ],
  },
  {
    version: 'D00.06.04#422',
    date: '31 octobre 2026',
    title: 'Sites, sauvegardes Drive et informations Plesk réunis',
    changes: [
      'Référencez les sites hébergés hors Plesk, dont les environnements Tosho, avec leur serveur, leur CMS et leur environnement.',
      'Les sites internes appartiennent à votre organisation ; les sites Clients restent rattachés à leur vraie fiche.',
      'Retrouvez les sites déclarés dans les listes générale et par serveur ainsi que dans les compteurs du tableau de bord.',
      'Les vhosts Plesk affichent leur état actif, inactif ou en sauvegarde/restauration, avec la date du relevé.',
      'Suivez automatiquement les sauvegardes Drive de chaque vhost et la sauvegarde générale du serveur, avec les bases, les incréments et une confirmation Plesk distincte.',
      'Autorisez la lecture des métadonnées Google puis indiquez le dossier Clients : les archives restent chiffrées et leur restauration continue à passer par Plesk.',
      'Les fiches vhosts reprennent les adresses IP, l’utilisateur de transfert et les versions WordPress retournées par WP Toolkit, sans récupérer de mot de passe.',
      'Le tableau de bord reprend les mises à jour de Plesk exposées par son API et les erreurs de lecture des sauvegardes. Les autres notifications restent consultables dans Plesk.',
    ],
  },
  {
    version: 'D00.06.04#421',
    date: '31 octobre 2026',
    title: 'Des sites plus lisibles et une collecte reliée à Plesk',
    changes: [
      'Sites observés s’ouvre sur une page complète avec Client et CMS alignés, davantage de place pour Stockage et un bouton d’actualisation par icône.',
      'La connexion Plesk lance les collectes sans activation supplémentaire : domaines et copies locales chaque heure, informations techniques quotidiennes. Une suspension reste respectée.',
      'Les relevés automatiques n’immobilisent plus la fiche du vhost. Les erreurs de lecture apparaissent près de l’action et vos saisies restent conservées.',
      'Une référence Freebe déjà liée permet de retrouver sa fiche Client ou de confirmer la relation existante sans recréer de doublon.',
      'Les sauvegardes distantes, leur destination et leur restauration restent à raccorder ; un inventaire local vide ne signifie pas absence de copies distantes.',
    ],
  },
  {
    version: 'D00.06.04#420',
    date: '30 octobre 2026',
    title: 'Observer les copies locales Plesk de chaque abonnement',
    changes: [
      'La fiche du vhost permet de lire les dates, tailles et échecs signalés des sauvegardes locales de son abonnement, en lecture seule.',
      'Sites observés affiche la dernière copie locale retournée, la taille des entrées locales et l’occupation disque du site, sans les confondre.',
      'Les lectures automatiques quotidiennes incluent cet inventaire local. Les derniers relevés réussis sont conservés après erreur et les accès historiques restent signalés.',
      'Un inventaire local vide ne signifie pas absence de sauvegardes distantes. Destination distante, planification, incrémental et restauration restent à vérifier.',
    ],
  },
  {
    version: 'D00.06.03#419',
    date: '30 octobre 2026',
    title: 'Des serveurs plus lisibles et des lectures Plesk planifiées',
    changes: [
      'Le registre, les sites et les hébergeurs reprennent les maquettes validées : bandeaux noirs, recherches crème, actions rouges, logos et boutons alignés.',
      'Retrouvez la version Plesk et ouvrez sa connexion depuis le suivi serveur. Le bouton œil avec le nombre de sites rejoint les actions sur une seule ligne.',
      'Un site peut appartenir à votre organisation sans créer de Client. Vos CMS, environnements et rattachements restent conservés lors des lectures.',
      'Activez les lectures automatiques dans les réglages Plesk : inventaire horaire et informations techniques quotidiennes, avec dates, suspension et conservation du dernier succès.',
      'Le tableau de bord affiche le CA encaissé HT du mois depuis Finances & stats, ainsi que les serveurs pilotés et les sites inventoriés depuis Serveurs reliés.',
      'La fiche Client présente une ligne Drive compacte avec le logo du connecteur ; la ligne Contact principal de la synthèse est retirée. Les actions à venir gardent leurs couleurs pleines.',
      'Les tags Production sont noirs, Développement violets et Archive gris. La santé système et les sauvegardes restent à raccorder.',
    ],
  },
  {
    version: 'D00.06.03#418',
    date: '30 octobre 2026',
    title: 'Qualifier les vhosts et lire leurs informations techniques Plesk',
    changes: [
      'Un seul bouton œil avec le nombre de sites ouvre la pop-in du serveur. La vue est plus compacte, avec des boutons proportionnés et sans logo propriétaire répété à chaque site.',
      'Renseignez le CMS, l’environnement et la date de création du vhost dans Tosho. WordPress et PrestaShop utilisent les logos fournis ; from scratch désigne explicitement un site sans CMS.',
      'Depuis la fiche du vhost, lancez une lecture Plesk de la création, du statut, de l’abonnement, du disque, du quota, de PHP, des bases et comptes associés, et des enregistrements DNS, selon les droits de la clé.',
      'Les déclarations Tosho sont conservées lors des nouvelles lectures. Les données Plesk sont datées ; une rubrique indisponible reste signalée et le dernier relevé réussi est conservé.',
      'Les environnements serviront aux futurs rattachements explicites des projets Clients à plusieurs vhosts et serveurs.',
      'Les fiches Clients retrouvent des marges internes, des titres harmonisés et des blocs mieux proportionnés. Les actions à venir gardent leurs couleurs pleines et restent inopérantes.',
    ],
  },
  {
    version: 'D00.06.03#417',
    date: '30 octobre 2026',
    title: 'Retrouver les sites de chaque serveur depuis son aperçu',
    changes: [
      'Le registre affiche le nombre de domaines réellement inventoriés et la date de lecture Plesk pour chaque serveur.',
      'L’icône œil ouvre l’aperçu du serveur inventorié ; retrouvez ses sites et choisissez leurs Clients dans Tosho.',
      'Le suivi des sites reprend le bandeau de compteurs, les filtres Client et propriétaire, et la structure du prototype.',
      'La santé système et les sauvegardes sont signalées comme non observées tant que leurs lectures ne sont pas raccordées.',
      'Les pop-in Serveurs utilisent la croix commune, visible sur le voile sombre ; fermer les réglages actualise les données locales.',
    ],
  },
  {
    version: 'D00.06.03#416',
    date: '30 octobre 2026',
    title: 'Retrouver les sites Plesk et les rattacher aux Clients',
    changes: [
      'La roue crantée ouvre les Réglages Plesk. Une aide indique comment créer la clé depuis l’extension officielle Plesk.',
      'Lancez la lecture des domaines depuis ces réglages ; le dernier inventaire réussi est conservé avec sa date.',
      'Ouvrez Sites observés pour rechercher un domaine, un Client ou un serveur et filtrer les résultats.',
      'Rattachez chaque site à son Client, indépendamment du propriétaire du serveur. Les logos Clients apparaissent lorsqu’ils sont disponibles.',
      'Les sites absents du dernier relevé restent dans l’historique. Les abonnements et sauvegardes restent à raccorder.',
    ],
  },
  {
    version: 'D00.06.02#415',
    date: '30 octobre 2026',
    title: 'Configurer un accès Plesk et choisir les technologies du serveur',
    changes: [
      'Sur les serveurs administrés, enregistrez une clé API Plesk dans l’accès protégé, remplacez-la ou révoquez son usage dans Tosho.',
      'Lancez un test ponctuel de lecture de la version Plesk. Les domaines, vhosts et sauvegardes restent à raccorder.',
      'Les champs OS serveur et Panneau de contrôle serveur proposent des listes avec les logos et les noms des technologies.',
      'Autre/Inconnu figure en premier et est sélectionné par défaut à la création ; les valeurs déjà renseignées restent conservées à la modification.',
      'Les déclarations des panneaux restent corrigibles et les listes sont utilisables au clavier.',
    ],
  },
  {
    version: 'D00.06.02#414',
    date: '30 octobre 2026',
    title: 'Gérer les hébergeurs et leurs logos',
    changes: [
      'Ouvrez le catalogue Hébergeurs depuis Serveurs reliés ; ajoutez un hébergeur manuellement ou choisissez un fournisseur Freebe.',
      'Modifiez le nom, ajoutez, remplacez ou retirez son logo, puis choisissez cet hébergeur dans le formulaire du serveur.',
      'Les hébergeurs déjà saisis sont repris et leurs logos apparaissent dans le registre. Les noms et logos locaux sont conservés lors des imports Freebe.',
      'Linux, Fedora et Windows Server rejoignent Debian et Ubuntu. Déclarez plusieurs panneaux : Plesk, Webmin, Virtualmin, ISPConfig, DirectAdmin, cPanel & WHM et Coolify.',
      'Chaque panneau garde son état déclaré présent, absent ou inconnu ; les déclarations Plesk existantes sont conservées.',
    ],
  },
  {
    version: 'D00.06.02#412',
    date: '30 octobre 2026',
    title: 'Reconnaître les responsables des serveurs',
    changes: [
      'Les colonnes Propriétaire et Administration affichent le logo disponible ; le nom prend le relais si le logo manque ou ne se charge pas.',
      'Les logos principalement clairs reçoivent un fond noir arrondi ; les logos sombres restent sans cadre.',
      'Un crayon identifie la modification, deux flèches circulaires accompagnent l’actualisation et la légende du tableau est mieux espacée.',
    ],
  },
  {
    version: 'D00.06.02#411',
    date: '30 octobre 2026',
    title: 'Compléter les repères de la fiche Client',
    changes: [
      'La fiche conserve les coordonnées, la provenance Freebe et les contacts, puis présente les blocs Projets, Relation client, SAV et FAQ.',
      'Les modules encore indisponibles sont signalés ; leurs actions restent désactivées et leurs indicateurs ne présentent aucun chiffre de démonstration.',
    ],
  },
  {
    version: 'D00.06.02#410',
    date: '30 octobre 2026',
    title: 'Renseigner plusieurs sites internet par Client',
    changes: [
      'Ajoutez, corrigez ou retirez plusieurs sites lors de la création et de la modification d’un Client ; tous les liens apparaissent sur sa fiche.',
      'La recherche retrouve aussi un Client par un site secondaire. Les adresses existantes et les coordonnées locales restent conservées lors des imports Freebe.',
    ],
  },
  {
    version: 'D00.06.02#408',
    date: '30 octobre 2026',
    title: 'Reconnaître les systèmes et les déclarations Plesk',
    changes: [
      'Le registre affiche les logos Debian, Ubuntu et Plesk pour mieux repérer les technologies déclarées.',
      'Une icône verte distingue Plesk déclaré présent, une rouge son absence déclarée ; les informations inconnues gardent un repère neutre.',
    ],
  },
  {
    version: 'D00.06.02#400',
    date: '29 octobre 2026',
    title: 'Consulter et renseigner les serveurs reliés',
    changes: [
      'Les Administrateurs peuvent consulter le registre, ajouter un serveur et corriger ses informations déclarées.',
      'Le propriétaire et l’administration restent distincts ; les hébergements Clients externes sont indiqués comme descriptifs.',
    ],
  },
  {
    version: 'D00.06.02#398',
    date: '28 octobre 2026',
    title: 'Préparer le registre des serveurs',
    changes: [
      'Les opérations du registre sont protégées pour les Administrateurs ; les informations restent déclaratives.',
      'Un Client propriétaire d’un serveur reste protégé contre une rétrogradation incompatible. L’écran du registre viendra ensuite.',
    ],
  },
  {
    version: 'D00.06.01#387',
    date: '28 octobre 2026',
    title: 'Refuser plus tôt les fichiers non pris en charge',
    changes: [
      'Les fichiers vides et les documents Google natifs sont refusés avant de réserver de l’espace pour leur envoi.',
      'Une ancienne préparation incompatible reste annulable avant envoi ; les dépôts clients restent fermés.',
    ],
  },
  {
    version: 'D00.06.01#385',
    date: '28 octobre 2026',
    title: 'Suivre et annuler une préparation de fichier',
    changes: [
      'Les contrôles serveur permettent de relire une confirmation ou d’annuler une préparation avant le début de l’envoi.',
      'Une préparation déjà démarrée reste protégée ; les dépôts clients restent fermés.',
    ],
  },
  {
    version: 'D00.06.01#383',
    date: '27 octobre 2026',
    title: 'Préparer les contrôles avant chaque envoi',
    changes: [
      'La préparation relie la vérification des dossiers privés et de l’espace disponible aux contrôles précédant chaque envoi.',
      'Un contrôle échoué ou annulé bloque l’envoi ; les dépôts clients restent fermés.',
    ],
  },
  {
    version: 'D00.06.01#381',
    date: '27 octobre 2026',
    title: 'Préparer le contrôle de capacité des transferts en cours',
    changes: [
      'La préparation vérifie l’espace disponible en conservant les réservations des transferts en cours.',
      'L’annulation est transmise aux contrôles préalables ; les dépôts clients restent bloqués.',
    ],
  },
  {
    version: 'D00.06.01#378',
    date: '27 octobre 2026',
    title: 'Relier la fin reçue à la vérification du fichier',
    changes: [
      'La préparation relie une fin enregistrée à la vérification puis à la confirmation du fichier, sans le renvoyer.',
      'Les transferts sans preuve de fin et les dépôts clients restent bloqués.',
    ],
  },
  {
    version: 'D00.06.01#376',
    date: '27 octobre 2026',
    title: 'Enregistrer la fin d’un transfert interrompu',
    changes: [
      'La préparation peut enregistrer une fin reçue avant une interruption du suivi, sans renvoyer le fichier.',
      'Les transferts sans preuve de fin et les dépôts clients restent bloqués.',
    ],
  },
  {
    version: 'D00.06.01#374',
    date: '27 octobre 2026',
    title: 'Conserver le constat de fin d’un transfert',
    changes: [
      'La préparation peut conserver une fin de transfert reçue avant une interruption du suivi.',
      'Ce constat ne relance aucun envoi ; les transferts incertains et les dépôts clients restent bloqués.',
    ],
  },
  {
    version: 'D00.06.01#371',
    date: '27 octobre 2026',
    title: 'Préparer le diagnostic des transferts interrompus',
    changes: [
      'La préparation peut interroger l’état d’un transfert interrompu sans transmettre son contenu.',
      'Ce constat ne relance aucun envoi ; les transferts incertains et les dépôts clients restent bloqués.',
    ],
  },
  {
    version: 'D00.06.01#369',
    date: '27 octobre 2026',
    title: 'Préparer la récupération des transferts terminés',
    changes: [
      'Un transfert dont la fin est déjà enregistrée peut être revérifié après une interruption.',
      'Aucun fichier n’est renvoyé ; les transferts incertains et les dépôts clients restent bloqués.',
    ],
  },
  {
    version: 'D00.06.01#367',
    date: '27 octobre 2026',
    title: 'Retrouver une confirmation de transfert',
    changes: [
      'La préparation permet de retrouver un transfert déjà confirmé après une réponse perdue.',
      'Les transferts incertains et les dépôts clients restent bloqués.',
    ],
  },
  {
    version: 'D00.06.01#365',
    date: '27 octobre 2026',
    title: 'Préparer la confirmation des fichiers transférés',
    changes: [
      'La préparation relie la vérification du fichier à sa confirmation.',
      'Les transferts incertains restent bloqués ; les dépôts clients ne sont pas ouverts.',
    ],
  },
  {
    version: 'D00.06.01#363',
    date: '26 octobre 2026',
    title: 'Relier le suivi aux transferts préparatoires',
    changes: [
      'La préparation enregistre l’intention avant chaque envoi et son résultat après le retour.',
      'Les transferts incertains restent bloqués pendant la préparation de la reprise.',
    ],
  },
  {
    version: 'D00.06.01#361',
    date: '26 octobre 2026',
    title: 'Des titres cohérents dans toutes les pages',
    changes: [
      'Les titres et sur-titres reprennent les tailles de la page Clients & prospects, sur ordinateur et mobile.',
      'Les sur-titres des pages utilisent le même rouge.',
    ],
  },
  {
    version: 'D00.06.01#359',
    date: '26 octobre 2026',
    title: 'Préparer le suivi durable des transferts',
    changes: [
      'La préparation conserve le suivi des transferts et distingue les envois dont le résultat reste incertain.',
      'La reprise et les dépôts clients restent fermés pendant le raccordement et les vérifications.',
    ],
  },
  {
    version: 'D00.06.01#357',
    date: '26 octobre 2026',
    title: 'Préparer la vérification des fichiers transférés',
    changes: [
      'La vérification prépare le contrôle du contenu, de la version et des droits du fichier après transfert.',
      'Les dépôts clients restent fermés pendant les derniers raccordements.',
    ],
  },
  {
    version: 'D00.06.01#355',
    date: '26 octobre 2026',
    title: 'Préparer le transfert des fichiers par blocs',
    changes: [
      'Le transfert prépare l’envoi par blocs et le contrôle des octets reçus après une interruption.',
      'Les dépôts clients restent fermés pendant le raccordement et les vérifications finales.',
    ],
  },
  {
    version: 'D00.06.01#353',
    date: '26 octobre 2026',
    title: 'Conserver la référence des futurs dépôts privés',
    changes: [
      'La préparation des fichiers conserve une référence stable pour retrouver une tentative interrompue.',
      'Le transfert et la reprise des fichiers restent à raccorder avant l’ouverture des dépôts clients.',
    ],
  },
  {
    version: 'D00.06.01#351',
    date: '26 octobre 2026',
    title: 'Coordonner la préparation des fichiers privés',
    changes: [
      'La préparation et la confirmation des versions sont coordonnées et tracées.',
      'Le parcours de dépôt reste à raccorder aux ressources métier autorisées.',
    ],
  },
  {
    version: 'D00.06.01#349',
    date: '25 octobre 2026',
    title: 'Préparer le registre des fichiers privés',
    changes: [
      'Le registre distingue chaque fichier de ses versions et conserve leurs références et leur intégrité.',
      'Les dépôts clients restent à raccorder aux ressources métier autorisées.',
    ],
  },
  {
    version: 'D00.06.01#347',
    date: '25 octobre 2026',
    title: 'Préparer les réservations d’espace Drive',
    changes: [
      'La préparation des futurs dépôts conserve désormais les réservations d’espace et leurs reprises.',
      'Le dépôt des fichiers clients reste à raccorder ; aucune limite n’est choisie automatiquement.',
    ],
  },
  {
    version: 'D00.06.01#343',
    date: '25 octobre 2026',
    title: 'Choisir les limites des futurs dépôts Drive',
    changes: [
      'Vous pouvez enregistrer l’espace à garder libre, la taille maximale d’un fichier et la durée de validité d’une mesure.',
      'Les trois valeurs restent à renseigner ; leur enregistrement n’active pas encore les dépôts.',
    ],
  },
  {
    version: 'D00.06.01#341',
    date: '24 octobre 2026',
    title: 'Actualiser l’espace Drive disponible',
    changes: [
      'Le bouton d’actualisation mesure l’espace global du compte Google à la demande et affiche la date du contrôle.',
    ],
  },
  {
    version: 'D00.06.01#339',
    date: '24 octobre 2026',
    title: 'Préparer les droits des fichiers privés',
    changes: [
      'Les contrôles des futurs fichiers privés sont préparés avant leur raccordement aux ressources métier.',
      'La rubrique Mémoire du menu est renommée Co-pilotage.',
    ],
  },
  {
    version: 'D00.06.01#338',
    date: '24 octobre 2026',
    title: 'Corriger la préparation des dossiers Drive',
    changes: [
      'La préparation des dossiers privés utilise désormais un accès compatible avec les droits Google actuels.',
    ],
  },
  {
    version: 'D00.06.01#337',
    date: '24 octobre 2026',
    title: 'Analyser un échec de préparation Drive',
    changes: [
      'Un échec de préparation des dossiers peut désormais être analysé plus précisément pour en rechercher la cause.',
    ],
  },
  {
    version: 'D00.06.01#335',
    date: '24 octobre 2026',
    title: 'Vérifier les dossiers privés d’un Client',
    changes: [
      'Dans Google Drive, l’administrateur peut choisir un Client existant puis préparer ou revérifier ses dossiers privés.',
      'Le résultat daté reste consultable et apparaît dans l’historique. Le dépôt de fichiers métier n’est pas encore activé.',
    ],
  },
  {
    version: 'D00.06.01#333',
    date: '23 octobre 2026',
    title: 'Préparation du stockage Client',
    changes: [
      'Les dossiers privés des Clients peuvent être préparés par l’administrateur. Le dépôt de fichiers reste à venir.',
    ],
  },
  {
    version: 'D00.06.01#331',
    date: '23 octobre 2026',
    title: 'Un bouton de fermeture plus discret',
    changes: [
      'Le bouton Fermer du lecteur vidéo est plus compact, tout en conservant une zone facile à toucher.',
    ],
  },
  {
    version: 'D00.06.01#330',
    date: '23 octobre 2026',
    title: 'Repère de version actualisé',
    changes: [
      'Le numéro affiché permet d’identifier cette livraison et de la retrouver dans l’historique.',
    ],
  },
  {
    version: 'D00.06.01#325',
    date: '23 octobre 2026',
    title: 'Google Drive : essayer la lecture d’une vidéo privée',
    changes: [
      'L’administrateur peut préparer une courte vidéo de test sur son Drive et la lire directement dans Tosho.',
      'Le lecteur permet d’avancer, de reculer et de fermer la vidéo. Aucune copie temporaire n’est conservée sur l’hébergement Tosho.',
      'La préparation et le rangement de la vidéo de test apparaissent dans l’historique. La lecture sur chaque appareil reste à vérifier.',
    ],
  },
  {
    version: 'D00.06.01#323',
    date: '23 octobre 2026',
    title: 'Google Drive : des raccourcis qui restent dans la page',
    changes: [
      'Connexion, Espace disponible, Stockage et Historique font défiler le connecteur sans revenir au tableau de bord.',
      'Sur mobile, les rubriques restent visibles sous le bandeau du haut. La préférence de mouvement réduit est respectée.',
    ],
  },
  {
    version: 'D00.06.01#321',
    date: '23 octobre 2026',
    title: 'Google Drive : conserver les résultats des tests',
    changes: [
      'L’historique du connecteur regroupe les connexions et les tests du stockage, avec leur date et le détail des cinq contrôles.',
      'Les tests précédents restent enregistrés. Un test interrompu et un simple nettoyage sont distingués d’un test complet réussi.',
      'Le dernier résultat déjà conservé est repris à sa date d’origine, sans avoir à refaire le test.',
    ],
  },
  {
    version: 'D00.06.01#317',
    date: '23 octobre 2026',
    title: 'Google Drive : vérifier le stockage privé',
    changes: [
      'Un bouton vérifie le dépôt et la relecture d’un petit fichier de test privé, puis sa mise à la corbeille et sa restauration.',
      'Chaque étape affiche un résultat daté. Le fichier de test est remis à la corbeille ; un nettoyage interrompu peut être repris.',
      'Aucun fichier client ni vidéo n’est utilisé. La lecture des vidéos et les réglages par client ou tableau restent à ajouter.',
    ],
  },
  {
    version: 'D00.06.01#313',
    date: '22 octobre 2026',
    title: 'Google Drive : retour de connexion protégé',
    changes: [
      'Les cartes Google Drive reprennent les tailles de texte, les espaces et les fonds noirs, blancs et beiges de Freebe.',
      'Le retour de Google protège les informations temporaires de connexion, y compris si le service est momentanément indisponible.',
      'Le dépôt et la lecture des fichiers restent à ajouter ; la connexion du compte KATANA reste à tester.',
    ],
  },
  {
    version: 'D00.06.01#312',
    date: '22 octobre 2026',
    title: 'Google Drive : connecter le compte de l’agence',
    changes: [
      'Depuis Connexions, l’Administrateur peut autoriser son compte Google puis vérifier son identité et son espace disponible dans Tosho.',
      'Les écrans reprennent les repères de Freebe, avec un état daté, des messages simples et l’historique des tentatives.',
      'Le dépôt et la lecture des fichiers, le Drive d’un client et les réglages par tableau restent à ajouter.',
    ],
  },
  {
    version: 'D00.05.06#310',
    date: '22 octobre 2026',
    title: 'Déclarations URSSAF : changement d’année fiabilisé',
    changes: [
      'L’import de l’année courante suit le calendrier de Paris dès minuit le 1er janvier, à la prévisualisation comme à la confirmation.',
    ],
  },
  {
    version: 'D00.05.04#308',
    date: '21 octobre 2026',
    title: 'Finances : cartes plus claires et calculs complétés',
    changes: [
      'Une forge animée accompagne le chargement et disparaît dès que les chiffres sont prêts.',
      'Les provisions sont regroupées en trois postes. Le détail des sources reste replié et le solde après provisions utilise aussi les historiques datés.',
      'Le conseil tient compte des provisions estimées toutes chiffrées. Un objectif de rémunération déjà atteint permet de conclure à un complément nul.',
      'Le classement des Clients suit la marge sur coûts connus décroissante. Le caractère partiel est rappelé une seule fois.',
      'Le CA et le total des sorties sont placés avant le diagramme des dépenses. La différence trompeuse entre encaissé et facturé est retirée.',
    ],
  },
  {
    version: 'D00.05.04#307',
    date: '21 octobre 2026',
    title: 'Finances : poursuite de l’accélération du chargement',
    changes: [
      'La lecture des factures et des soldes bancaires est allégée. Les sources restent contrôlées pour chaque demande.',
      'Un ancien montant de facture ou plusieurs comptes bancaires ne sont jamais confondus avec une donnée actuelle et certaine.',
    ],
  },
  {
    version: 'D00.05.04#306',
    date: '21 octobre 2026',
    title: 'Finances : historique plus léger à calculer',
    changes: [
      'Le chargement évite de parcourir et reconvertir les mêmes mouvements pour chaque mois de l’historique.',
      'Les résultats restent datés et recalculés depuis les sources actuelles à chaque demande, sans conserver un ancien bilan financier.',
    ],
  },
  {
    version: 'D00.05.04#305',
    date: '21 octobre 2026',
    title: 'Finances : calculs S5, lecture et chargement',
    changes: [
      'Les pourcentages de la barre à 100 % alternent au-dessus et en dessous. Les barres de chaque catégorie sont rétablies et les textes respectent un minimum de 12 px.',
      'Le réglage de l’objectif sur fond noir garde son clic et son accès au clavier, sans contour décoratif.',
      'Les ratios des dépenses utilisent le CA encaissé HT du même mois. Le point zéro distingue les charges, les catégories mixtes et le besoin de remboursement d’emprunt.',
      'Les provisions connues, la projection de TVA trimestrielle et les limites du conseil de rémunération sont calculées. Un règlement non affecté reste à établir dans les sources.',
      'Les coûts communs connus sont répartis entre Clients selon leurs recettes HT encaissées ; la marge partielle reste distincte des futurs coûts de Projet et de temps.',
      'Les lectures répétées des opérations sont regroupées dans chaque calcul, sans conserver un bilan financier périmé entre deux demandes.',
    ],
  },
  {
    version: 'D00.05.04#303',
    date: '21 octobre 2026',
    title: 'TVA : dépôt des CA3 et dépenses regroupées',
    changes: [
      'Un bloc Déclarations TVA, sous celui de l’URSSAF, reçoit les PDF téléchargés sur le site des impôts. Un raccourci en haut de page y mène directement.',
      'Les périodes, déductions et crédits des CA3 sont lus automatiquement. Un nouveau téléchargement du même dépôt ne crée pas de doublon. Le net déclaré apparaît dans les provisions ; son paiement reste suivi séparément.',
      'Les dépenses sont regroupées en une ligne par catégorie : couleur, pictogramme, montant, pourcentage et caractère fixe ou variable. Une barre à 100 % les représente en haut de la carte, rémunération connue incluse.',
      'Les réglages des dépenses s’ouvrent directement. La lecture répétée des mêmes PDF URSSAF est évitée pour alléger les calculs de la page.',
      'Le dépôt TVA est manuel et indépendant de Freebe. Les obligations complètes, le conseil et les marges restent à fiabiliser dans S5.',
    ],
  },
  {
    version: 'D00.05.04#301',
    date: '21 octobre 2026',
    title: 'Provisions : restants fournisseurs et TVA trimestrielle visible',
    changes: [
      'Les états de règlement déjà renseignés dans Freebe alimentent le restant fournisseur connu. Un achat soldé n’est pas ajouté une nouvelle fois aux provisions.',
      'Les photographies prises après le mois consulté ne servent pas à inventer un ancien état de paiement. Les incohérences de montants restent signalées dans la couverture.',
      'La TVA collectée sur les recettes du trimestre apparaît dans le tableau, séparément des paiements classés du mois. La TVA nette, les crédits et les échéances demeurent à établir.',
      'La progression vers l’objectif est arrondie au centième de pourcentage. Les mois et montants du graphique restent proches des barres lorsque le cumul négatif est faible.',
    ],
  },
  {
    version: 'D00.05.04#300',
    date: '21 octobre 2026',
    title: 'Finances : progression vers l’objectif et calculs suivis par sprint',
    changes: [
      'La carte de fond de roulement suit le cumul annuel des flux vers votre objectif réglé, au lieu de répéter le solde bancaire. Vert : progression ; rouge : recul ; gris : stable ou inconnu.',
      'Les deux graphiques de rémunération portent les titres demandés et le réglage sur fond sombre reste visible.',
      'Un diagramme en pourcentages des paiements connus par catégorie complète les dépenses, avec les mêmes couleurs et pictogrammes dans le tableau. Les montants restent dans le tableau.',
      'Chaque calcul indisponible indique son sprint responsable ; les raccordements et critères de recette sont suivis dans la feuille de route.',
      'Les calculs de TVA nette, obligations complètes, conseil et coûts encore inachevés ne sont pas présentés comme terminés.',
    ],
  },
  {
    version: 'D00.05.04#299',
    date: '21 octobre 2026',
    title: 'URSSAF : conserver les règlements indiqués dans Freebe',
    changes: [
      'Les indications de règlement présentes dans l’export Déclarations sont conservées sans modifier les anciens imports ni les pointages.',
      'Une déclaration indiquée payée dans Freebe est distinguée des débits bancaires confirmés. Son montant officiel provient toujours du PDF.',
      'L’estimation URSSAF et versement libératoire sur le CA déjà encaissé apparaît dans le tableau des provisions.',
      'Les PDF sont lus automatiquement ; aucune nouvelle vérification manuelle des déclarations n’est demandée.',
      'Les écarts bancaires historiques, la TVA nette et le registre complet des obligations restent ouverts.',
    ],
  },
  {
    version: 'D00.05.04#297',
    date: '20 octobre 2026',
    title: 'Finances : soldes Freebe datés et graphiques cohérents',
    changes: [
      'La lecture du solde suit les libellés de la carte bancaire Freebe même lorsque son statut apparaît en dernier.',
      'Les jauges et graphiques partagent le solde Freebe observé ou reconstitué le plus récent du mois, sans prolonger une ancienne observation.',
      'Les soldes BNP et manuels ne servent plus de secours aux statistiques. La saisie du solde de secours est retirée de la connexion Freebe.',
      'Les montants des barres annuelles rouges restent lisibles en blanc et les réglages utilisent la même icône.',
      'TVA nette, obligations restantes et conseil de prélèvement demeurent indisponibles quand leurs sources ne permettent pas le calcul.',
    ],
  },
  {
    version: 'D00.05.04#296',
    date: '20 octobre 2026',
    title: 'Freebe : lecture des comptes affichés sans lien',
    changes: [
      'La lecture du solde reconnaît aussi le nom de compte affiché en texte, avec sa date de synchronisation.',
      'Chaque compte reste identifié séparément ; une valeur absente ou ambiguë ne produit aucun solde fictif.',
    ],
  },
  {
    version: 'D00.05.04#295',
    date: '20 octobre 2026',
    title: 'Freebe : lecture du solde et reprise des exports absents',
    changes: [
      'La lecture du solde accepte le nom de compte fourni par la banque et conserve sa date de synchronisation.',
      'Un export qui ne se télécharge pas est réessayé une fois, avec les mêmes contrôles de fichier.',
    ],
  },
  {
    version: 'D00.05.03#293',
    date: '15 octobre 2026',
    title: 'Finances : couleurs lisibles et explications discrètes',
    changes: [
      'Les dépenses annuelles retrouvent leurs couleurs et une légende chiffrée sur les mêmes recettes et dépenses.',
      'Les barres de rémunération annuelles indiquent le signe du flux de trésorerie connu : vert positif, rouge négatif, gris nul, inconnu ou futur.',
      'Les explications secondaires se déplient à la demande ; les chiffres essentiels restent visibles.',
      'Le faux repère Super mois sans seuil est retiré de la jauge. Un encaissement dépassant son échelle est signalé sans changer les objectifs.',
    ],
  },
  {
    version: 'D00.05.03#292',
    date: '15 octobre 2026',
    title: 'Finances : choix du mois et trésorerie datée',
    changes: [
      'Le mois de CA choisi reste sélectionné pendant le chargement ; les anciens chiffres sont masqués et les erreurs apparaissent dans la carte.',
      'La carte CA retrouve ses lignes prévues, sans les cumuls annuels, comparaisons et indicateurs supplémentaires ajoutés.',
      'Un import tardif ne prolonge plus les soldes de trésorerie au-delà des dernières données bancaires disponibles.',
    ],
  },
  {
    version: 'D00.05.03#291',
    date: '15 octobre 2026',
    title: 'Dépenses annuelles : graphique lisible et calculs explicités',
    changes: [
      'Les dépenses annuelles se lisent en euros par mois, avec le nombre de mois clos disponibles et le détail du calcul pour chaque année.',
      'Le mois en cours et les années futures ne faussent plus les moyennes ; aucun classement par couleur ne prétend juger la rentabilité.',
      'Les débours restent dans le détail mensuel mais sont exclus du ratio comparé aux encaissements clients.',
      'Les moyennes et pourcentages sont arrondis correctement ; des dépenses incomplètes ne donnent pas de pourcentage trompeur.',
      'Le compteur de rémunération distingue les mois connus des mois terminés.',
    ],
  },
  {
    version: 'D00.05.03#290',
    date: '14 octobre 2026',
    title: 'Finances : bases de calcul et suivis des recettes fiabilisés',
    changes: [
      'Les débours sont exclus du chiffre d’affaires ; les montants partiels affichent leur couverture.',
      'Les recettes HT, TTC et TVA disposent de cumuls cohérents et d’une comparaison annuelle sur les mêmes mois clos.',
      'Les seuils d’activité et de prélèvement sont estimés à partir des charges qualifiées et des taux documentés.',
      'La ventilation URSSAF conserve les montants et arrondis des pièces, y compris historiques. Les paiements attribués restent distincts des versements du mois.',
      'Dans Connexions / API, Actualiser les calculs relit les factures identiques d’un export fourni en conservant les factures et leur pointage.',
      'Le conseil de prélèvement, la TVA nette et les résultats dépendant d’échéances manquantes restent explicitement non calculables.',
    ],
  },
  {
    version: 'D00.05.03#288',
    date: '14 octobre 2026',
    title: 'Finances : lectures corrigées et calculs mieux qualifiés',
    changes: [
      'Les dates d’achat françaises et les bases monétaires Freebe sont interprétées explicitement ; les imports historiques sont conservés.',
      'Les déclarations URSSAF suivent leur période officielle ; les acomptes et les soldes sont distingués dans le chiffre d’affaires facturé.',
      'Les montants payés dont le HT est inconnu ne servent plus à un pourcentage HT ; les sous-totaux restent identifiés.',
      'Le conseil de rémunération et le seuil conseillé attendent le raccordement des obligations et des charges : aucun montant non justifié n’est présenté.',
      'Les scénarios futurs et les flux conservés sont expliqués ; les couleurs annuelles non justifiées sont neutralisées.',
    ],
  },
  {
    version: 'D00.05.03#286',
    date: '13 octobre 2026',
    title: 'Finances : cinq raccourcis en mode vertical et graphique de trésorerie lisible',
    changes: [
      'Quand les colonnes Finances & Stats passent en une seule colonne, cinq raccourcis donnent directement accès aux rubriques dans l’ordre de la page.',
      'Sur ordinateur et tablette hors mode vertical, les trois raccourcis existants restent inchangés.',
      'Le graphique de l’évolution du fonds de roulement affiche désormais les montants bancaires datés sous chaque mois, au plus près des barres.',
      'Les douze mois peuvent défiler horizontalement sur petits écrans ; les données absentes restent indiquées sans montants fictifs.',
    ],
  },
  {
    version: 'D00.05.03#285',
    date: '13 octobre 2026',
    title: 'Finances : raccourcis centrés sur smartphone',
    changes: [
      'Sur smartphone, les raccourcis des sections financières sont centrés et le libellé Raccourcis est masqué.',
      'Sur tablette et ordinateur, leur présentation est inchangée.',
    ],
  },
  {
    version: 'D00.05.03#284',
    date: '13 octobre 2026',
    title: 'Finances : courbe corrigée et suivi mensuel du fonds de roulement',
    changes: [
      'La courbe des rémunérations et ses pastilles suivent les variations ensemble, plus haut que les barres, avec un contour affiné.',
      'La carte Évolution du fond de roulement utilise un fond sombre et une icône verte.',
      'Les mois affichent les soldes bancaires datés, les écarts en euros et les pourcentages lorsque les deux mois sont connus.',
      'Un réglage sécurisé permet de fixer un objectif annuel propre à l’organisation et affiche sa moyenne mensuelle.',
      'Les soldes provisoires restent distingués du fonds de roulement réel, non calculable avant l’audit des formules.',
    ],
  },
  {
    version: 'D00.05.03#280',
    date: '13 octobre 2026',
    title: 'Finances : nouveaux cadrans et accès à la démonstration',
    changes: [
      'Les cartes À garder de côté et Ce qui vous rapporte restent visibles en mode réel, sans faux chiffres.',
      'La carte Évolution du fond de roulement affiche les douze mois de l’année ; ses seuils réels attendent la revue financière.',
      'Le Superviseur peut autoriser la démonstration par Administrateur, indépendamment des autres comptes.',
      'Les repères du CA, les pastilles mensuelles et les contrastes des valeurs ont été réorganisés.',
    ],
  },
  {
    version: 'D00.05.03#279',
    date: '13 octobre 2026',
    title: 'Finances : corrections visuelles et clients démo',
    changes: [
      'Les libellés de la jauge de chiffre d’affaires suivent leurs repères ; leur rotation s’adapte à la largeur.',
      'Les titres, les espaces des graphiques et les explications de pourcentage sont plus lisibles.',
      'Les commandes des cartes restent en tête tant que le titre leur laisse assez de place.',
      'Les fenêtres de réglage conservent une croix seule et davantage d’espacement.',
      'La carte de rentabilité en démo utilise les noms et logos de l’annuaire Clients ; ses montants restent fictifs.',
    ],
  },
  {
    version: 'D00.05.03#277',
    date: '13 octobre 2026',
    title: 'Finances : lisibilité, jauges et réglages',
    changes: [
      'Les raccourcis défilent vers les rubriques sans quitter les finances.',
      'Les sélecteurs et réglages passent en bas des cartes quand la place manque. Les légendes et les textes sont harmonisés.',
      'Les pop-in utilisent un fond noir plus intense et une fermeture à l’extérieur du panneau.',
      'Les dépenses annuelles affichent leur part dans les recettes : les couleurs dépendent de ce rapport.',
      'Les historiques bancaires reconstitués sont distingués des soldes observés ; les données manquantes restent signalées.',
      'Les propositions de provisions précisent les échéances mensuelles et la TVA trimestrielle.',
    ],
  },
  {
    version: 'D00.05.03#276',
    date: '13 octobre 2026',
    title: 'Dépenses et propositions de nouvelles cartes',
    changes: [
      'Vos dépenses disposent de leur propre sélecteur de mois, avec les catégories justifiées dans Freebe et les montants disponibles.',
      'Chaque type de dépense peut être qualifié comme fixe, variable ou mixte ; ce choix est conservé dans Tosho.',
      'La carte Par année présente la moyenne mensuelle des dépenses payées sur les mois disponibles.',
      'Le mode Données démo propose deux nouvelles cartes : À garder de côté et Ce qui vous rapporte, à valider ensemble.',
    ],
  },
  {
    version: 'D00.05.03#275',
    date: '13 octobre 2026',
    title: 'Trésorerie et navigation du cockpit',
    changes: [
      'La carte Trésorerie reprend le nouveau prototype : année au choix, douze jauges et soldes bancaires datés disponibles.',
      'Les réglages permettent de choisir les seuils de couleur en euros et l’objectif de réserve, avec des préconisations ajustables.',
      'Le chiffre d’affaires dispose de son propre sélecteur de mois et conserve des repères de jauge stables.',
      'Les raccourcis pointent vers les titres de rubrique ; le titre de page est harmonisé avec Clients et Prestataires.',
    ],
  },
  {
    version: 'D00.05.03#273',
    date: '10 octobre 2026',
    title: 'Solde Freebe et secours bancaire',
    changes: [
      'Le solde bancaire Freebe dispose de sa propre mise à jour et rejoint les mises à jour globale et planifiée.',
      'Un solde manuel daté peut être saisi en secours. Freebe reprend la priorité dès qu’un solde synchronisé aussi récent est disponible.',
      'Une demande de code MFA pendant une acquisition Freebe est signalée par la cloche et par mail, avec saisie dans une pop-in Tosho.',
    ],
  },
  {
    version: 'D00.05.03#272',
    date: '10 octobre 2026',
    title: 'Finances & Stats : choix du mois et première carte CA',
    changes: [
      'Le sélecteur de mois permet de consulter les rémunérations et les chiffres des mois importés, notamment août et septembre.',
      'La première carte de chiffre d’affaires reprend le dégradé clair, les textes et les illustrations du dernier prototype XD.',
      'Les icônes fournies sont intégrées aux cartes et à leurs réglages.',
      'Les calculs exploitent les mouvements et les opérations sans compter les paiements deux fois. Le CA encaissé HT utilise les montants de TVA importés.',
      'Le forgeron et le samouraï restent monochromes blancs et fixes pour éviter la déformation de la bourse.',
    ],
  },
  {
    version: 'D00.05.03#271',
    date: '10 octobre 2026',
    title: 'Rémunération : correction du rendu XD',
    changes: [
      'Les libellés des graphiques restent verticaux à −90°, comme dans le prototype.',
      'Les trois cartes et la pop-in de réglage reprennent la composition XD ; les blocs non prototypés ont été retirés.',
      'Le forgeron et le samouraï retrouvent leur illustration monochrome blanche et leur animation d’origine corrigée.',
      'La pop-in et les légendes s’adaptent aux petits écrans. Les données réelles restent séparées du mode démo.',
    ],
  },
  {
    version: 'D00.05.03#270',
    date: '9 octobre 2026',
    title: 'Ma rémunération : les cartes du prototype XD',
    changes: [
      'La rémunération se lit en trois cartes : situation du mois, évolution mensuelle et moyennes année après année.',
      'Les calculs utilisent les opérations Freebe et les soldes BNP datés. Les mois inconnus restent distincts des zéros ; 2020 couvre septembre à décembre.',
      'Le conseil de versement affiche ses hypothèses et les projections prolongent uniquement l’objectif choisi.',
      'La réserve et les six couleurs de la jauge se règlent dans la trésorerie. Une aide explique les couleurs des montants.',
      'Les cartes s’adaptent au mobile et l’échange du katana et de la bourse a été corrigé.',
    ],
  },
  {
    version: 'D00.05.03#269',
    date: '3 octobre 2026',
    title: 'Finances & stats : plus de chiffres Freebe visibles',
    changes: [
      'Les factures chiffrées restent visibles en sous-total lorsque d’autres pièces du mois manquent de montant HT, avec leur couverture explicite.',
      'Les entrées, sorties et catégories d’opérations Freebe alimentent le cockpit sans être confondues avec les encaissements BNP.',
      'La catégorie « Rémunérations dirigeants » est reconnue dans l’historique ; le dernier PDF URSSAF disponible reste daté.',
    ],
  },
  {
    version: 'D00.05.03#268',
    date: '3 octobre 2026',
    title: 'Finances & stats : même lecture en fictif et en réel',
    changes: [
      'Le cockpit réel reprend les cartes, les graphiques et les rubriques de l’aperçu fictif, dans le même ordre.',
      'Les montants réels viennent de Freebe, BNP et du PDF URSSAF ; les calculs encore indisponibles restent explicitement en attente.',
      'Les objectifs et la réserve se règlent dans les cartes réelles, et le détail des sources reste accessible plus bas.',
    ],
  },
  {
    version: 'D00.05.03#267',
    date: '3 octobre 2026',
    title: 'Finances & stats : jauge et réglages plus lisibles',
    changes: [
      'La jauge de trésorerie reste dans la carte de réserve et ne déborde plus du bandeau principal.',
      'Les réglages des objectifs prennent place en bas à droite, dans un cercle plus discret.',
    ],
  },
  {
    version: 'D00.05.03#266',
    date: '3 octobre 2026',
    title: 'Finances & stats : seuil de réserve et montants Freebe',
    changes: [
      'Les ventes et achats facturés HT de Freebe sont visibles en montants, sans les confondre avec les encaissements.',
      'Le seuil de réserve se règle dans la carte, et une graduation illustre le solde bancaire face à ce seuil.',
      'Le salaire visé et l’objectif de CA mensuel se règlent dans leurs cartes ; les pop-in montrent un conseil Tosho fondé sur les chiffres disponibles et les méthodes URSSAF et Bpifrance.',
      'La rémunération réellement prise vient de la catégorie comptable Freebe « rémunération dirigeant » et apparaît sur les mois précédents.',
      'La grange illustrée et l’échange du katana animent le cockpit ; les jauges se remplissent verticalement. L’historique distingue un seul solde daté des séries et conserve le PDF URSSAF comme référence.',
    ],
  },
  {
    version: 'D00.05.03#265',
    date: '3 octobre 2026',
    title: 'Finances & stats : historique vivant',
    changes: [
      'Les soldes BNP datés et les volumes de pièces Freebe des six derniers mois dessinent la première vue réelle des tendances.',
      'Les scènes japonaises et les animations sont visibles dans le cockpit réel, avec respect du mouvement réduit.',
      'Le dernier PDF URSSAF trouvé sur cette période est présenté avec son mois ; aucun chiffre fictif ne se mêle aux données réelles.',
    ],
  },
  {
    version: 'D00.05.03#264',
    date: '3 octobre 2026',
    title: 'Finances & stats : cockpit illustré et comparaison annuelle',
    changes: [
      'Le CA déclaré est comparé au même mois de l’année précédente quand les deux PDF URSSAF officiels sont disponibles.',
      'Les cartes du cockpit alternent rouge, noir et blanc, avec deux scènes japonaises animées dans l’aperçu fictif.',
      'La version affichée correspond à cette livraison ; les chiffres fictifs restent signalés.',
    ],
  },
  {
    version: 'D00.05.03#263',
    date: '3 octobre 2026',
    title: 'Finances & stats : premier cockpit v23',
    changes: [
      'Le nouveau panneau est intégré à Tosho avec la palette, les pictogrammes et les sources consultables.',
      'Les montants de rémunération, réserve, projets et prévisions restent dans un aperçu fictif en attendant les calculs réels.',
    ],
  },
  {
    version: 'D00.05.03#260',
    date: '2 octobre 2026',
    title: 'Finances & stats : parcourir tous les flux',
    changes: [
      'Les mouvements et opérations Freebe ainsi que les lignes BNP peuvent être chargés par pages au-delà des 500 premiers résultats de chaque mois.',
      'Le chargement des pages suivantes conserve les choix en cours et les liens déjà établis restent visibles.',
      'La recette sur données réelles et les indicateurs financiers sourcés restent à venir ; aucun rapprochement automatique n’est introduit.',
    ],
  },
  {
    version: 'D00.05.03#257',
    date: '2 octobre 2026',
    title: 'Finances & stats : liaisons explicites des flux',
    changes: [
      'Les écrans Administrateur permettent de choisir un mouvement Freebe, ses opérations et une ligne BNP facultative, même lorsque les dates relèvent de mois différents.',
      'Les lignes déjà liées ou présentes dans des exports BNP recouvrants sont signalées ; un retrait nécessite une confirmation et conserve son historique.',
      'Un montant cohérent ne confirme pas le chiffre d’affaires. Les indicateurs, la recette sur données réelles et la reprise de l’ergonomie restent à venir.',
    ],
  },
  {
    version: 'D00.05.03#254',
    date: '1er octobre 2026',
    title: 'Finances & stats : liens Freebe et BNP sécurisés',
    changes: [
      'Une base d’attribution explicite et auditée relie les opérations et mouvements Freebe aux lignes BNP, sans déduire de lien d’un montant ou d’une date similaires.',
      'Les réimports Freebe divergents et les exports BNP recouvrants rendent un rapprochement périmé ou ambigu, sans effacer son historique.',
      'Cette livraison pose les contrôles de données ; l’écran d’attribution et les indicateurs restent à venir. Une égalité des montants ne confirme pas le chiffre d’affaires.',
    ],
  },
  {
    version: 'D00.05.03#251',
    date: '1er octobre 2026',
    title: 'Finances & stats : débits URSSAF rapprochés de BNP',
    changes: [
      'Les débits BNP d’un mois choisi peuvent être attribués explicitement à une déclaration URSSAF, puis retirés si nécessaire.',
      'Un même débit observé dans plusieurs exports est bloqué ou signalé ; les attributions conservent un historique.',
      'Le PDF URSSAF fournit toujours le montant dû. Un total bancaire égal au dû ne constitue pas à lui seul une preuve de règlement auprès de l’URSSAF.',
    ],
  },
  {
    version: 'D00.05.03#247',
    date: '1er octobre 2026',
    title: 'Finances & stats : sources et déclarations URSSAF',
    changes: [
      'La rubrique Finances & stats affiche les sources du mois, le solde BNP à sa date et les indicateurs encore à rapprocher.',
      'Les montants et la période des déclarations proviennent automatiquement des PDF URSSAF. Un écart avec Freebe est signalé pour correction à la source.',
      'Les cotisations dues restent distinctes des paiements bancaires ; aucun montant non rapproché n’est présenté comme confirmé.',
    ],
  },
  {
    version: 'D00.05.02#245',
    date: '1er octobre 2026',
    title: 'Historique bancaire plus lisible',
    changes: [
      'Le panneau de l’historique BNP est blanc avec des titres lisibles, tandis que les sources conservées restent sur des lignes sombres.',
    ],
  },
  {
    version: 'D00.05.02#243',
    date: '1er octobre 2026',
    title: 'ZIP BNP depuis Finder et finitions des logos',
    changes: [
      'Un ZIP BNP créé en compressant un dossier sur Mac peut contenir les relevés PDF et l’export XLS sans réorganisation manuelle.',
      'Les quatre cartes de logos partagent le même fond, avec une palette dans l’en-tête et une simple coche après enregistrement.',
    ],
  },
  {
    version: 'D00.05.02#241',
    date: '1er octobre 2026',
    title: 'Import bancaire ZIP et écrans de connexion harmonisés',
    changes: [
      'Le connecteur BNP accepte désormais un ZIP contenant un export XLS et plusieurs relevés PDF, avec aperçu et confirmation avant import.',
      'L’historique bancaire distingue les opérations extraites de l’Excel des PDF conservés comme justificatifs privés ; un bouton permet d’ouvrir chaque pièce.',
      'Les logos des connecteurs utilisent le gabarit 450 × 200 px ; leur aperçu s’actualise immédiatement et un message confirme chaque modification.',
      'Les écrans de connexion et la configuration du Superviseur reprennent les couleurs et les contrôles du reste de Tosho.',
    ],
  },
  {
    version: 'D00.05.02#239',
    date: '30 septembre 2026',
    title: 'Connexion bancaire et logos des connecteurs',
    changes: [
      'La connexion BNP possède son propre écran pour prévisualiser et importer les opérations .xls et les relevés PDF du seul compte professionnel, avec un historique des confirmations.',
      'Les relevés sont privés et les montants importés restent à rapprocher avant tout affichage financier confirmé.',
      'Le Superviseur peut personnaliser les logos des connecteurs depuis Préférences & réglages, sur un gabarit commun, et rétablir chaque logo par défaut.',
    ],
  },
  {
    version: 'D00.05.02#237',
    date: '30 septembre 2026',
    title: 'Finances & stats : préparation des rapprochements',
    changes: [
      'Les sources Freebe et BNP ont été qualifiées pour préparer des indicateurs financiers vérifiables.',
      'Les avoirs, remboursements et références ambiguës sont isolés avant tout calcul de chiffre d’affaires. Aucun nouvel indicateur chiffré n’est encore affiché.',
    ],
  },
  {
    version: 'D00.05.02#235',
    date: '30 septembre 2026',
    title: 'Déclarations URSSAF : suivi de l’import',
    changes: [
      'Chaque import ZIP des déclarations apparaît dans l’historique Freebe, y compris une réimportation sans nouvelle ligne ; les archives déjà importées sont reprises.',
      'Un commutateur choisit l’historique complet ou l’année courante, avec une ancre directe vers la carte URSSAF.',
      'La synchronisation globale et planifiée ne comprennent pas encore les déclarations URSSAF.',
    ],
  },
  {
    version: 'D00.05.02#233',
    date: '30 septembre 2026',
    title: 'Déclarations URSSAF : importer les preuves Freebe',
    changes: [
      'Depuis Connexions / API, l’Administrateur peut prévisualiser et importer un ZIP Déclarations Freebe contenant un JSON et ses PDF, sur tout l’historique ou l’année courante.',
      'Les pièces sont conservées dans un espace privé. Les périodes, pièces absentes et statuts brouillon sont signalés ; les montants Freebe ne sont pas encore certifiés ni rapprochés des paiements bancaires.',
      'Les déclarations restent distinctes de la synchronisation globale automatique.',
    ],
  },
  {
    version: 'D00.04.07#228',
    date: '30 septembre 2026',
    title: 'Remplacement des logos et clôture S4',
    changes: [
      'Un nouveau logo des Clients ou des Prestataires apparaît immédiatement après son enregistrement, avec une confirmation visible.',
      'Les photos des Contacts et les petits logos des cartes se rafraîchissent également après modification.',
    ],
  },
  {
    version: 'D00.04.06#227',
    date: '30 septembre 2026',
    title: 'Cartes Contacts et bas du menu affinés',
    changes: [
      'Les Contacts Clients et Prestataires ont une photo centrale, un logo superposé et des actions plus discrètes.',
      'La liste des Clients est plus compacte, le surtitre Provenance retrouve le rouge Tosho et le pied du menu prend moins de place.',
      'L’illustration de la forge est plus lumineuse dans la fenêtre Pourquoi Tosho.',
    ],
  },
  {
    version: 'D00.04.05#226',
    date: '29 septembre 2026',
    title: 'Présentation des annuaires harmonisée',
    changes: [
      'Les textes courants et les boutons des écrans Clients, Prospects et Prestataires utilisent des tailles cohérentes.',
      'Les fiches alternent surfaces claires et sombres, avec des alertes lisibles en haut des écrans.',
      'Une fiche Prestataire peut revenir à l’état Fournisseur après confirmation, tout en conservant ses coordonnées et documents.',
    ],
  },
  {
    version: 'D00.04.04#225',
    date: '29 septembre 2026',
    title: 'Recette des annuaires et chiffres clés',
    changes: [
      'Le tableau de bord affiche le nombre réel de Prospects Freebe et de Clients qualifiés, avec les mêmes icônes que le menu.',
      'Le compteur des Clients historiques ouvre leur liste ; plusieurs fiches peuvent être corrigées en Prospect après confirmation, en conservant devis, factures et contacts.',
      'Les recherches, retours et messages sont plus lisibles. Le menu Clients ramène à l’accueil du répertoire.',
      'Les Prestataires ont leurs cartes sur l’écran principal, des contacts avec photo et des actions plus compactes.',
    ],
  },
  {
    version: 'D00.04.03#224',
    date: '29 septembre 2026',
    title: 'Répertoires Clients et Prestataires',
    changes: [
      'Les 281 références Freebe forment la liste des Prospects, dont les Clients déjà qualifiés. Les recherches, le dernier import et les Clients historiques sont visibles dès le répertoire.',
      'Une fiche Client historique peut être corrigée en Prospect si aucune facture ou relation active ne lui est liée. L’édition de la fiche fonctionne de nouveau.',
      'Les fournisseurs fusionnés n’apparaissent qu’une fois. Les Prestataires sont présentés en cartes bleues avec logo, devis PDF privés et actions plus compactes.',
      'Les Contacts peuvent avoir une photo et leurs suggestions Freebe peuvent être masquées définitivement pour un Client.',
    ],
  },
  {
    version: 'D00.04.02#223',
    date: '29 septembre 2026',
    title: 'Parcours Clients, Prospects et Prestataires',
    changes: [
      'Les compteurs ouvrent des listes recherchables ; la qualification des devis et le choix des références Freebe restent en accès secondaire.',
      'Depuis Freebe, une recherche et une sélection précèdent la vérification de l’identité et de la relation Client.',
      'Un Prospect devient Client sur confirmation humaine, et une qualification Client erronée peut être corrigée si aucun Contact ou compte ne lui est rattaché.',
      'Le lien confirmé avec un destinataire de devis conserve le devis historique et évite de compter deux fois ce Prospect.',
      'Les Prestataires disposent du même répertoire et d’une sélection secondaire depuis les Fournisseurs Freebe.',
    ],
  },
  {
    version: 'D00.04.01#221',
    date: '28 septembre 2026',
    title: 'Clients, Contacts et Prestataires Tosho',
    changes: [
      'Les Administrateurs peuvent créer des fiches Clients confirmés depuis Freebe ou manuellement, rechercher les doublons et conserver leurs corrections locales.',
      'Les Contacts et Prestataires sont ajoutés après confirmation humaine ; une invitation nominative nécessite une adresse vérifiée.',
      'Les devis clients émis depuis Freebe peuvent être qualifiés pour le ratio statistique prospects/Clients, sans créer de fiche Prospect ni de droit d’accès.',
      'Les huit familles Freebe sont disposées sur deux rangées de quatre cartes sur grand écran.',
    ],
  },
  {
    version: 'D00.02.58#217',
    date: '28 septembre 2026',
    title: 'Opérations et Mouvements Freebe intégrés',
    changes: [
      'Les historiques Opérations JSON et Mouvements XLSX peuvent désormais être contrôlés puis importés depuis leurs cartes dédiées.',
      'Leurs mises à jour fonctionnent seules, dans la synchronisation globale et selon la planification hebdomadaire.',
      'Les dépôts et téléchargements Freebe sont acceptés jusqu’à 15 Mio pour toutes les familles, avec les protections XLSX conservées.',
      'La planification se règle désormais uniquement avec une heure et les jours cochés ; le champ « Tous les X jours » a été retiré.',
      'Les logos de Connexions utilisent maintenant une taille harmonisée, notamment Google Drive.',
    ],
  },
  {
    version: 'D00.02.56#214',
    date: '28 septembre 2026',
    title: 'Périmètre Freebe clarifié',
    changes: [
      'La carte Trésorerie est retirée : Freebe ne fournit pas de jeu de données Trésorerie autonome.',
      'Les deux prochaines familles à intégrer sont désormais clairement Opérations et Mouvements.',
    ],
  },
  {
    version: 'D00.02.55#212',
    date: '28 septembre 2026',
    title: 'Mise à jour des Devis fiabilisée',
    changes: [
      'La mise à jour Devis se termine désormais après l’export Freebe, depuis sa carte comme depuis la synchronisation globale.',
      'Les devis historiques restent conservés lorsqu’un export annuel ne reprend que les devis de l’année courante.',
      'Le compteur de la carte affiche « devis » sans ajouter un second s.',
    ],
  },
  {
    version: 'D00.02.54#210',
    date: '28 septembre 2026',
    title: 'Mise à jour automatique des Devis et navigation clarifiée',
    changes: [
      'Les cartes Freebe, Trello et Google Drive occupent désormais toute la largeur disponible sans laisser de marge artificielle à droite.',
      'Les Devis peuvent désormais être mis à jour automatiquement depuis Freebe, seuls, dans la synchronisation globale ou selon la planification.',
      'Le Tableau de bord rejoint la rubrique Pilotage du menu latéral pour tous les profils concernés.',
    ],
  },
  {
    version: 'D00.02.53#208',
    date: '28 septembre 2026',
    title: 'Synchronisation Freebe plus claire et Connexions enrichies',
    changes: [
      'La planification explique maintenant qu’une reconnexion Freebe vérifiée est nécessaire avant son activation et mène directement au bon réglage.',
      'Une synchronisation globale continue après l’échec isolé d’une famille, tandis que l’historique nomme correctement la famille concernée.',
      'Google Drive apparaît dans Connexions comme intégration à venir pour les vidéos privées des FAQ, tutoriels et de la Knowledge Base.',
      'La feuille de route utilise désormais des critères de sortie vérifiables et numérote Finances comme Sprint 3.',
      'Le menu latéral Administrateur appelle désormais Réglages la zone Configuration, Connexions / API et Serveurs reliés.',
    ],
  },
  {
    version: 'D00.02.52#206',
    date: '26 septembre 2026',
    title: 'Sélection multi-jours corrigée et Devis XLSX importables',
    changes: [
      'La sélection de plusieurs jours de planification reste stable : cocher une deuxième case ne provoque plus d’écran blanc.',
      'L’historique Devis XLSX peut maintenant être déposé depuis la carte Devis puis appliqué atomiquement dans une bibliothèque dédiée.',
      'Les 602 références historiques restent identifiées par leur référence Freebe, sans rattachement automatique fondé sur le seul nom du client.',
    ],
  },
  {
    version: 'D00.02.51#204',
    date: '26 septembre 2026',
    title: 'Planification Freebe récurrente et préparation des Devis XLSX',
    changes: [
      'La planification des mises à jour reste active après une exécution réussie et propose les jours de la semaine, du lundi au vendredi par défaut.',
      'Le lecteur de l’export Devis XLSX est préparé et contrôlé sur l’historique ; l’import des devis dans la bibliothèque sera livré dans le prochain palier.',
    ],
  },
  {
    version: 'D00.02.50#201',
    date: '26 septembre 2026',
    title: 'Historique Freebe préservé et ventes débloquées',
    changes: [
      'Les mises à jour annuelles de Clients, Contacts, Fournisseurs, Factures d’achat et Factures de vente conservent désormais explicitement tout l’historique déjà importé.',
      'Un débours de vente qui référence un achat absent ne bloque plus les autres ventes et ne crée aucune pièce fictive.',
      'Les fichiers temporaires restent purgés après lecture pour préserver l’espace disque, tandis que les données structurées et leur traçabilité restent conservées.',
    ],
  },
  {
    version: 'D00.02.49#200',
    date: '25 septembre 2026',
    title: 'Factures de vente Freebe opérationnelles',
    changes: [
      'Les factures, avoirs, acomptes et soldes de vente peuvent maintenant être importés depuis l’historique JSON Freebe.',
      'La mise à jour seule, la synchronisation globale et la planification enchaînent désormais les Factures de vente après les Factures d’achat.',
      'Chaque document conserve son client source et ses liens de débours vers les achats déjà importés.',
    ],
  },
  {
    version: 'D00.02.48#199',
    date: '25 septembre 2026',
    title: 'Mise à jour Freebe et état de session fiabilisés',
    changes: [
      'La mise à jour des Factures d’achat reconnaît désormais le libellé JSON réellement affiché par Freebe, tout en conservant la variante précédente.',
      'Le statut distingue maintenant une session enregistrée d’une connexion vérifiée en direct et affiche la date du dernier contrôle réussi.',
      'Les trois accordéons de consolidation Fournisseurs sont repliés par défaut pour alléger la page.',
    ],
  },
  {
    version: 'D00.02.47#198',
    date: '25 septembre 2026',
    title: 'Application des Factures d’achat débloquée',
    changes: [
      'La reprise Factures d’achat est maintenant journalisée avec sa propre famille avant le démarrage de l’application métier.',
      'La transaction ne s’interrompt plus au moment de créer la ligne d’historique du dépôt manuel.',
      'Chaque nouvelle tentative Factures d’achat apparaît désormais dans l’historique avec son état réel.',
    ],
  },
  {
    version: 'D00.02.46#197',
    date: '25 septembre 2026',
    title: 'Import des Factures d’achat et menu latéral corrigés',
    changes: [
      'Un historique Factures d’achat autonome peut désormais réutiliser les fournisseurs déjà présents dans la bibliothèque Tosho.',
      'Après validation, les Factures d’achat sont effectivement appliquées par leur adaptateur métier dédié.',
      'Un rejet pendant l’application est maintenant distingué d’un fichier JSON invalide afin d’indiquer la bonne cause.',
      'Le menu Administrateur suit la nouvelle organisation : Pilotage, Mémoire avec Finances & stats, puis Administration avec Connexions / API et Serveurs reliés.',
    ],
  },
  {
    version: 'D00.02.45#195',
    date: '25 septembre 2026',
    title: 'Import historique des Factures d’achat débloqué',
    changes: [
      'Le serveur accepte désormais réellement les exports Factures d’achat jusqu’à 10 Mio, au lieu de les interrompre à 1 Mio avant leur contrôle.',
      'Le type de fichier Factures d’achat est maintenant enregistré comme flux JSON brut, comme les trois autres familles déjà opérationnelles.',
      'Un contrôle automatique couvre explicitement un historique Factures d’achat supérieur à l’ancienne limite.',
    ],
  },
  {
    version: 'D00.02.44#194',
    date: '25 septembre 2026',
    title: 'Historique Factures d’achat volumineux corrigé',
    changes: [
      'Les exports JSON de Factures d’achat sont désormais acceptés jusqu’à 10 Mio dans l’interface, conformément à la limite du serveur.',
      'Les confirmations et erreurs de cette carte parlent maintenant correctement des Factures d’achat et non des Fournisseurs.',
      'La mise à jour Factures d’achat déplie désormais la rubrique Documents de Freebe avant de lancer son export JSON.',
      'Le bouton Gérer la consolidation fait défiler la page sans modifier l’URL ni quitter l’écran Connexions.',
      'La carte Fournisseurs affiche le total consolidé utilisé par les statistiques, tout en conservant le nombre de fiches Freebe source.',
      'Une navigation secondaire discrète donne accès aux cinq grandes zones de la page, et un bouton flottant permet de remonter en haut des pages longues.',
      'La mise à jour isolée et la synchronisation globale affichent également les libellés complets des Factures d’achat.',
    ],
  },
  {
    version: 'D00.02.43#192',
    date: '25 septembre 2026',
    title: 'Factures d’achat et consolidation Fournisseurs finalisées',
    changes: [
      'Les factures d’achat Freebe peuvent maintenant être importées depuis un historique, mises à jour seules ou incluses dans la synchronisation globale et planifiée.',
      'Chaque achat conserve son fournisseur source afin de préparer les analyses de coûts sans perdre le détail Freebe.',
      'La consolidation Fournisseurs distingue durablement les rapprochements refusés, permet de passer à la suggestion suivante et conserve la possibilité de défusionner.',
      'L’écran de consolidation est placé sous l’historique des imports, accessible depuis la carte Fournisseurs et entièrement lisible sur fond sombre.',
    ],
  },
  {
    version: 'D00.02.42#189',
    date: '25 septembre 2026',
    title: 'Fournisseurs regroupés sans perdre les sources',
    changes: [
      'Tosho regroupe automatiquement les fiches Fournisseurs uniquement lorsqu’un identifiant juridique stable apporte une certitude suffisante.',
      'Les rapprochements de noms proches sont proposés dans une fenêtre où l’Administrateur choisit les fiches et leur nom commun.',
      'Chaque regroupement reste visible et un fournisseur peut être défusionné sans supprimer ni modifier sa fiche Freebe source.',
      'Les prochains achats et les statistiques pourront agréger les coûts par fournisseur commun tout en conservant le détail de chaque facture.',
    ],
  },
  {
    version: 'D00.02.41#186',
    date: '25 septembre 2026',
    title: 'Import historique Fournisseurs corrigé',
    changes: [
      'Le dépôt d’un export historique Fournisseurs est désormais reconnu et transmis correctement à la prévalidation.',
      'Sur chaque famille opérationnelle, Parcourir et Importer l’historique sont regroupés avant l’action indépendante de mise à jour.',
      'Les cartes utilisent maintenant des icônes adaptées aux personnes, aux documents et aux données financières.',
      'La carte Récurrences a été retirée de cet écran tant qu’aucun export Freebe qualifié ne justifie son intégration.',
    ],
  },
  {
    version: 'D00.02.40#183',
    date: '25 septembre 2026',
    title: 'Fournisseurs Freebe opérationnels',
    changes: [
      'La page Freebe permet désormais d’importer l’historique Fournisseurs et de lancer sa mise à jour récente.',
      'La synchronisation globale et la planification quotidienne traitent maintenant Clients, Contacts puis Fournisseurs successivement.',
      'Chaque Fournisseur reste dans une bibliothèque indépendante et ne devient jamais automatiquement un Prestataire Tosho.',
      'Les compteurs, la dernière mise à jour et l’historique distinguent clairement les traitements Fournisseurs.',
    ],
  },
  {
    version: 'D00.02.39#179',
    date: '23 septembre 2026',
    title: 'Écran Freebe clarifié',
    changes: [
      'Le bouton supérieur indique désormais clairement Synchro de MAJ globale, sans sous-texte redondant.',
      'Les nombres de Clients et de Contacts sont mis en évidence et la date de dernière mise à jour apparaît sur une ligne distincte.',
      'L’introduction explique les liens futurs entre les données Freebe, les projets, les FAQ, les SAV, le temps passé et le suivi financier.',
      'Le référentiel du futur pilotage financier prévoit aussi les budgets moyens par typologie de projet, notamment vitrine et e-commerce.',
    ],
  },
  {
    version: 'D00.02.38#177',
    date: '23 septembre 2026',
    title: 'Imports Freebe Clients et Contacts finalisés',
    changes: [
      'Chaque carte Clients et Contacts affiche désormais son nombre d’éléments et la date et l’heure de sa dernière mise à jour réussie.',
      'Les historiques complets Clients et Contacts peuvent être importés directement depuis leur carte avec Parcourir puis Importer l’historique.',
      'Chaque famille dispose de son propre bouton de mise à jour, tandis que Tout synchroniser traite Clients puis Contacts successivement.',
      'La planification quotidienne applique le même ordre Clients puis Contacts et conserve un bilan distinct pour chaque famille.',
      'L’import du fichier Contacts complet a été renforcé pour traiter fiablement les exports Freebe volumineux.',
    ],
  },
  {
    version: 'D00.02.37#175',
    date: '23 septembre 2026',
    title: 'Carte Contacts Freebe disponible',
    changes: [
      'La page Connexions affiche désormais une carte dédiée aux Contacts rattachés aux Clients Freebe.',
      'Un bouton Parcourir permet de déposer l’historique Contacts au format JSON.',
      'L’action de mise à jour annuelle Contacts est préparée et restera activée lorsque son acquisition sera raccordée.',
    ],
  },
  {
    version: 'D00.02.36#171',
    date: '23 septembre 2026',
    title: 'Historique Freebe compact et consultable',
    changes: [
      'L’historique affiche cinq tentatives par défaut afin de ne plus allonger indéfiniment la page.',
      'Les tentatives supplémentaires restent disponibles par groupes de cinq, sans supprimer aucune trace.',
      'Sur smartphone, chaque tentative se replie en une ligne de synthèse dont le rapport peut être ouvert à la demande.',
      'Les noms Mouvements et Récurrences restent lisibles dans les cartes Freebe sans coupure au milieu des mots.',
    ],
  },
  {
    version: 'D00.02.35#169',
    date: '23 septembre 2026',
    title: 'Intégration Freebe lisible sur iPhone',
    changes: [
      'La page Freebe reste maintenant contenue dans l’écran sur smartphone, notamment sur iPhone 13.',
      'L’historique des imports devient une suite de fiches lisibles sur petit écran au lieu d’un large tableau horizontal.',
      'La planification, les prérequis, les champs et les boutons s’adaptent désormais jusqu’aux grands iPhone.',
      'L’écran indique clairement que Clients est le seul flux actuellement exécuté et que les récurrences sont reportées à un sprint dédié.',
    ],
  },
  {
    version: 'D00.02.34#164',
    date: '22 septembre 2026',
    title: 'Publications visibles sans purge du cache',
    changes: [
      'La page principale est désormais relue à chaque navigation afin d’afficher la dernière version publiée sans vider manuellement le cache du navigateur.',
      'Les fichiers déjà versionnés conservent leur mise en cache rapide sans pouvoir maintenir un ancien écran de connexion.',
      'Chaque publication vérifie maintenant cette règle avant d’être déclarée disponible sur DEV.',
    ],
  },
  {
    version: 'D00.02.33#161',
    date: '22 septembre 2026',
    title: 'Mises à jour Clients Freebe planifiées',
    changes: [
      'La fréquence et l’heure des mises à jour Clients se règlent dans les paramètres Freebe, avec l’heure de Paris et la prochaine exécution affichées.',
      'La planification est désactivée par défaut et requiert une connexion Freebe active ; elle peut être interrompue à tout moment.',
      'Les mises à jour planifiées apparaissent dans le même historique que les imports manuels et les lancements immédiats, y compris lorsqu’elles échouent avant l’export.',
      'Chaque mise à jour automatique conserve la période annuelle de Freebe et laisse les Clients historiques absents du fichier intacts.',
    ],
  },
  {
    version: 'D00.02.32#159',
    date: '22 septembre 2026',
    title: 'Historique manuel, mises à jour Freebe sans calendrier',
    changes: [
      'L’historique Clients complet reste chargé par le fichier JSON exporté manuellement du 19 septembre 2020 à aujourd’hui.',
      'Les mises à jour automatiques utilisent désormais directement la période annuelle proposée par Freebe, sans ouvrir ni modifier son calendrier.',
      'Un export annuel est appliqué comme un lot incrémental : les clients historiques absents du fichier restent intacts dans la bibliothèque.',
      'La page distingue l’import historique complet de la mise à jour Clients courante et rend visibles les autres familles Freebe encore à intégrer.',
    ],
  },
  {
    version: 'D00.02.31#158',
    date: '22 septembre 2026',
    title: 'Dates Freebe appliquées par le sélecteur natif',
    changes: [
      'Tosho ne tente plus de saisir du texte dans les champs de date que Freebe rend volontairement non modifiables.',
      'La période du 19 septembre 2020 à aujourd’hui est maintenant transmise par le même événement que lors d’une sélection humaine dans le calendrier Freebe.',
      'Le filtre réellement consommé par le bouton JSON est contrôlé sur ses deux bornes avant que l’export puisse démarrer.',
      'Un wrapper de calendrier différent reste pris en charge par l’action officielle de mise à jour des filtres, toujours sans accepter un simple affichage trompeur.',
    ],
  },
  {
    version: 'D00.02.30#157',
    date: '22 septembre 2026',
    title: 'Filtre historique Freebe réellement reconnu',
    changes: [
      'Tosho reconnaît désormais les objets Date que Freebe crée après une saisie valide, au lieu de prendre ce bon état pour un refus.',
      'Le filtre utilisé par le bouton d’export est retrouvé depuis le champ visible du dialogue avant toute recherche de repli dans l’application.',
      'Le navigateur sécurisé applique explicitement le fuseau Europe/Paris afin d’aligner le calendrier affiché et les bornes réellement envoyées.',
      'Si l’état interne du filtre reste inaccessible, l’historique le distingue maintenant d’un simple refus de saisie et aucune donnée n’est modifiée.',
    ],
  },
  {
    version: 'D00.02.29#156',
    date: '22 septembre 2026',
    title: 'Période historique Freebe indépendante du fuseau serveur',
    changes: [
      'Tosho écrit désormais les deux bornes historiques directement dans la requête réellement utilisée par la fenêtre d’export Freebe.',
      'La fin de journée courante est calculée en heure de Paris puis convertie en instant UTC exact, en heure d’été comme en heure d’hiver.',
      'Le décalage horaire du navigateur sécurisé ne peut plus transformer la date de fin en lendemain ni faire refuser la période avant le téléchargement.',
      'La protection contre les exports Clients incomplets reste active : aucune bibliothèque n’est modifiée si Freebe renvoie moins de fiches que celles déjà connues.',
    ],
  },
  {
    version: 'D00.02.28#155',
    date: '22 septembre 2026',
    title: 'Export historique Freebe fiabilisé à la source',
    changes: [
      'Tosho modifie désormais le véritable filtre `createdAfter` utilisé par Freebe, et plus seulement la valeur visible dans le champ de date.',
      'La date de fin conservée par Freebe est contrôlée avant le déclenchement de chaque export Clients.',
      'Un export automatique qui omet des fiches déjà connues est refusé sans modifier la bibliothèque.',
      'L’historique indique alors clairement le nombre de clients reçus et le total attendu au lieu de rester sur « À vérifier ».',
    ],
  },
  {
    version: 'D00.02.27#154',
    date: '21 septembre 2026',
    title: 'Date historique réellement appliquée dans Freebe',
    changes: [
      'Tosho force désormais le sélecteur Freebe à enregistrer la date du 19 septembre 2020 dans son propre état, au-delà du simple affichage du champ.',
      'Les événements de saisie, de changement et de sortie du champ sont reproduits avant toute demande de fichier.',
      'La valeur réellement conservée reste contrôlée et aucun export n’est lancé si Freebe revient au 1er janvier 2026.',
      'L’import manuel validé sur 281 Clients reste disponible à tout moment comme solution de secours.',
    ],
  },
  {
    version: 'D00.02.26#153',
    date: '21 septembre 2026',
    title: 'Import Clients manuel de secours',
    changes: [
      'Un champ « Parcourir » permet désormais de charger un export Clients JSON obtenu manuellement depuis Freebe.',
      'Le fichier suit exactement les mêmes contrôles, rapprochements et écritures de bibliothèque que l’import automatique.',
      'Le dépôt reste disponible lorsqu’une reconnexion Freebe est nécessaire et apparaît comme « Dépôt manuel » dans l’historique.',
      'Le fichier source est limité à 1 Mio, traité en mémoire et n’est jamais conservé par Tosho.',
    ],
  },
  {
    version: 'D00.02.25#152',
    date: '21 septembre 2026',
    title: 'Saisie des dates Freebe compatible avec les champs verrouillés',
    changes: [
      'Tosho déverrouille temporairement les champs de dates en lecture seule avant d’y envoyer de véritables frappes clavier.',
      'Le verrouillage Freebe est restauré immédiatement après la saisie et la période reste contrôlée avant l’export.',
      'Une saisie refusée est retentée de façon bornée puis expliquée clairement dans l’historique.',
      'Les codes techniques d’échec ne sont plus affichés directement dans le tableau Administrateur.',
    ],
  },
  {
    version: 'D00.02.24#151',
    date: '21 septembre 2026',
    title: 'Export Freebe historique et imports contrôlables',
    changes: [
      'Tosho saisit désormais réellement dans Freebe la période du 19 septembre 2020 à aujourd’hui, au lieu de modifier seulement l’affichage des dates.',
      'La période est vérifiée avant de demander le fichier Clients afin d’éviter silencieusement un nouvel export limité à 2026.',
      'Un import encore actif peut être arrêté directement depuis sa ligne dans l’historique.',
      'Une tentative arrêtée reste visible avec l’état « Annulé » et ne peut plus appliquer de nouvelles données.',
    ],
  },
  {
    version: 'D00.02.23#150',
    date: '21 septembre 2026',
    title: 'Connexion Freebe indépendante de l’import',
    changes: [
      'Une connexion Freebe réussie reste désormais active même si Freebe modifie ensuite son écran d’export.',
      'Tosho vérifie la restauration de la session chiffrée sans lancer un export invisible pendant la connexion.',
      'L’import renseigne automatiquement la période du 19 septembre 2020 à aujourd’hui : aucune date ne doit être saisie manuellement.',
      'La détection des champs de dates accepte maintenant les conteneurs imbriqués de la fenêtre Freebe.',
    ],
  },
  {
    version: 'D00.02.22#149',
    date: '21 septembre 2026',
    title: 'Bibliothèque Clients Freebe indépendante',
    changes: [
      'Tosho récupère désormais les Clients Freebe depuis la création de KATANA jusqu’à aujourd’hui.',
      'Les entrées importées alimentent une bibliothèque de pré-clients sans créer automatiquement de fiches Clients Tosho.',
      'Les fiches déjà reliées restent conservées, tandis que leurs informations Freebe continuent d’être actualisées séparément.',
      'L’historique d’import distingue maintenant clairement les entrées ajoutées ou actualisées dans la bibliothèque.',
    ],
  },
  {
    version: 'D00.02.21#148',
    date: '21 septembre 2026',
    title: 'Progression des imports plus lisible',
    changes: [
      'Une roue animée distingue désormais immédiatement un import en cours d’un import terminé ou bloqué.',
      'L’historique précise successivement la mise en file, le téléchargement et le contrôle, puis l’écriture des fiches Clients.',
      'Le bilan distingue le nombre de lignes réellement reçues de Freebe des fiches créées, actualisées ou inchangées dans Tosho.',
      'L’indicateur disparaît automatiquement dès que le traitement atteint son résultat final et respecte la préférence de réduction des animations.',
    ],
  },
  {
    version: 'D00.02.20#147',
    date: '21 septembre 2026',
    title: 'Import Clients tolérant aux fiches Freebe incomplètes',
    changes: [
      'Une fiche Freebe identifiée n’empêche plus tout l’import lorsqu’un nom, un e-mail ou un identifiant légal optionnel est absent ou imparfait.',
      'Tosho conserve la fiche avec les données valides disponibles et pourra l’enrichir lors d’un import ultérieur.',
      'En cas de nouveau blocage, l’historique affiche désormais le véritable bilan de la simulation Clients au lieu du seul contrôle technique du fichier.',
    ],
  },
  {
    version: 'D00.02.19#146',
    date: '21 septembre 2026',
    title: 'Clients Freebe enregistrés dans Tosho',
    changes: [
      'Après validation de l’export, Tosho crée ou actualise désormais les fiches Clients correspondantes dans le périmètre de l’organisation.',
      'Chaque import terminé affiche directement le nombre de fiches créées, mises à jour et inchangées.',
      'L’historique se rafraîchit pendant le traitement afin de rendre son résultat visible sans ouvrir prématurément l’annuaire Clients.',
    ],
  },
  {
    version: 'D00.02.18#145',
    date: '21 septembre 2026',
    title: 'Export Clients adapté au dialogue Freebe actuel',
    changes: [
      'Tosho sélectionne désormais l’action complète « Liste des clients en json », qui déclenche directement le téléchargement dans l’interface Freebe actuelle.',
      'L’ancien dialogue en deux étapes « JSON » puis « Exporter » reste pris en charge.',
      'La sélection reste limitée à une cible visible et unique dans la fenêtre d’export.',
    ],
  },
  {
    version: 'D00.02.17#144',
    date: '21 septembre 2026',
    title: 'Connexion Freebe reconnue après identification',
    changes: [
      'L’entrée Connexions ouvre désormais un écran dédié avec des cartes séparées pour Freebe et Trello ; les réglages de chaque service resteront sur leur propre page.',
      'Tosho capture désormais la session Freebe sur le tableau de bord et sur le service de connexion utilisé pendant l’identification.',
      'La vérification peut ainsi réutiliser la connexion réussie au lieu de revenir à tort au statut « Connexion à finaliser ».',
      'Si Freebe refuse malgré tout la capture, Tosho indique maintenant clairement que la connexion a réussi mais que la session complète n’a pas pu être conservée.',
    ],
  },
  {
    version: 'D00.02.16#142',
    date: '21 septembre 2026',
    title: 'Session Freebe restaurée avant l’export',
    changes: [
      'Tosho restaure maintenant la session Freebe directement dans le navigateur de contrôle avant d’ouvrir le tableau de bord.',
      'Le contrôle peut ainsi atteindre la page Exports sans être renvoyé vers une nouvelle connexion.',
      'Aucun export manuel n’est demandé et un échec d’acquisition affiche désormais une explication précise sans conserver de fichier.',
    ],
  },
  {
    version: 'D00.02.15#140',
    date: '21 septembre 2026',
    title: 'Export Clients Freebe corrigé',
    changes: [
      'Tosho rejoint directement la page réelle des exports Freebe après la connexion.',
      'La rubrique CRM est ouverte uniquement lorsqu’elle est repliée, puis Tosho sélectionne l’export Clients au format JSON.',
      'Aucun export manuel n’est demandé : une session expirée ou une interface inattendue interrompt toujours le traitement sans conserver de fichier.',
    ],
  },
  {
    version: 'D00.02.14#138',
    date: '20 septembre 2026',
    title: 'Session Freebe de 30 jours prise en charge',
    changes: [
      'La connexion fonctionne avec ou sans double authentification, selon ce que Freebe présente réellement au compte.',
      'L’annonce 2FA visible dans le tableau de bord ne bloque plus l’export ; seule une véritable étape de connexion demande une intervention.',
      'Lorsque Freebe propose la 2FA, Tosho rappelle de conserver la session pendant 30 jours afin de permettre les imports quotidiens autonomes.',
    ],
  },
  {
    version: 'D00.02.13#136',
    date: '20 septembre 2026',
    title: 'Parcours de connexion Freebe fiabilisé',
    changes: [
      'Le navigateur sécurisé utilise désormais le parcours d’authentification actuel de Freebe afin d’éviter une seconde identification inutile.',
      'Si Freebe exige l’activation de la double authentification, Tosho l’indique clairement et ne tente jamais de la contourner.',
      'L’écran de l’intégration affiche maintenant le résultat réel de la tentative au lieu de conserver indéfiniment un message de vérification.',
    ],
  },
  {
    version: 'D00.02.12#134',
    date: '20 septembre 2026',
    title: 'Canal sécurisé Freebe corrigé',
    changes: [
      'Le navigateur sécurisé établit désormais correctement son canal d’affichage au lieu de recevoir une erreur serveur.',
      'Le contrôle de la session Tosho reste effectué avant l’ouverture du canal noVNC vers le navigateur temporaire.',
      'L’e-mail et le mot de passe restent saisis directement chez Freebe et ne sont jamais transmis ni stockés par Tosho.',
    ],
  },
  {
    version: 'D00.02.11#129',
    date: '20 septembre 2026',
    title: 'Connexion au navigateur Freebe rétablie',
    changes: [
      'Le navigateur sécurisé rejoint désormais correctement la session distante au lieu d’afficher un échec de connexion au serveur.',
      'Le canal d’affichage noVNC emprunte un chemin unique, authentifié et réservé à la tentative Freebe en cours.',
      'L’e-mail et le mot de passe restent saisis directement chez Freebe et ne sont jamais transmis ni stockés par Tosho.',
    ],
  },
  {
    version: 'D00.02.10#127',
    date: '20 septembre 2026',
    title: 'Affichage du navigateur Freebe débloqué',
    changes: [
      'Le navigateur sécurisé quitte désormais correctement son écran de chargement et affiche la page de connexion Freebe.',
      'La règle de sécurité nécessaire à son interface reste limitée à ce navigateur temporaire ; les protections de Tosho restent inchangées.',
      'L’Administrateur continue de saisir directement son e-mail et son mot de passe chez Freebe, sans transmission ni stockage par Tosho.',
    ],
  },
  {
    version: 'D00.02.09#125',
    date: '20 septembre 2026',
    title: 'Connexion Freebe actuelle vérifiée',
    changes: [
      'Le navigateur sécurisé ouvre désormais le parcours de connexion actuellement publié par Freebe, avec la vérification de l’adresse e-mail puis le mot de passe.',
      'Un test réel du navigateur exige l’apparition du champ e-mail Freebe avant chaque publication.',
      'Seuls les domaines Freebe restent accessibles dans ce navigateur temporaire ; aucun parcours Google ou Auth0 n’est utilisé.',
    ],
  },
  {
    version: 'D00.02.08#123',
    date: '20 septembre 2026',
    title: 'Connexion Freebe interactive',
    changes: [
      'Le navigateur temporaire affiche désormais correctement le formulaire natif de connexion Freebe par e-mail et mot de passe.',
      'La fenêtre Chromium reste visible et cadrée dans le navigateur sécurisé pendant toute l’intervention de l’Administrateur.',
      'Le texte d’aide sous le bouton « Annuler » est mieux espacé et les autres sites restent bloqués.',
    ],
  },
  {
    version: 'D00.02.07#121',
    date: '20 septembre 2026',
    title: 'Chargement de la connexion Freebe débloqué',
    changes: [
      'Le navigateur temporaire autorise désormais les sous-domaines Freebe nécessaires à l’affichage du formulaire de connexion natif.',
      'Le texte du bouton de connexion reste blanc et lisible au survol, au clic et après une visite précédente.',
      'Les autres sites restent bloqués dans ce navigateur sécurisé et le mot de passe demeure saisi uniquement chez Freebe.',
    ],
  },
  {
    version: 'D00.02.06#119',
    date: '20 septembre 2026',
    title: 'Connexion Freebe native corrigée',
    changes: [
      'L’Administrateur se connecte désormais avec l’e-mail et le mot de passe de son compte Freebe, sans parcours Google.',
      'Le mot de passe reste saisi uniquement sur la page Freebe du navigateur temporaire et n’est jamais demandé ni stocké par Tosho.',
      'Le navigateur sécurisé s’ouvre correctement et les textes des cartes sombres sont à nouveau lisibles.',
    ],
  },
  {
    version: 'D00.02.05#114',
    date: '20 septembre 2026',
    title: 'Import Freebe contrôlé sans conservation de fichier',
    changes: [
      'La connexion Freebe devient active uniquement après la réussite d’un export Clients de contrôle.',
      'L’Administrateur peut lancer à la demande une acquisition qui alimente le rapport de prévalidation existant, sans créer ni modifier de donnée métier.',
      'Chaque fichier téléchargé est lu en mémoire puis supprimé avec la session temporaire ; l’import suivant purge également tout téléchargement précédent.',
    ],
  },
  {
    version: 'D00.02.04#111',
    date: '20 septembre 2026',
    title: 'Autorisation Freebe supervisée',
    changes: [
      'L’Administrateur peut ouvrir un navigateur temporaire sécurisé pour se connecter directement à Freebe sans confier son mot de passe à Tosho.',
      'La session Freebe minimale est chiffrée et peut être remplacée, annulée ou révoquée depuis le parcours d’intégration.',
      'L’import automatique reste indisponible tant qu’un export de contrôle n’a pas confirmé la session capturée.',
    ],
  },
  {
    version: 'D00.02.03#108',
    date: '19 septembre 2026',
    title: 'Connexion Freebe visible pour l’Administrateur',
    changes: [
      'L’Administrateur peut renseigner l’adresse e-mail de son compte Freebe depuis le nouvel écran Connexions.',
      'L’historique des tentatives d’import est désormais consultable dans Tosho.',
      'Le lancement immédiat reste protégé et indisponible tant que la connexion Freebe n’est pas finalisée.',
    ],
  },
  {
    version: 'D00.02.02#105',
    date: '19 septembre 2026',
    title: 'Historique sécurisé des imports Freebe',
    changes: [
      'Tosho dispose désormais du socle permettant d’historiser les contrôles des imports Freebe manuels et automatiques.',
      'Les fichiers sont identifiés sans conserver leurs données métier brutes dans les rapports de contrôle.',
      'Aucune facture, aucun devis, aucun achat ni aucune statistique n’est encore créé ou modifié par ce lot.',
    ],
  },
  {
    version: 'D00.02.01#102',
    date: '19 septembre 2026',
    title: 'Préparation des imports Freebe reprise',
    changes: [
      'Le contrôle préalable des exports Freebe JSON et CSV est réintroduit par étapes sur un socle isolé.',
      'Aucune donnée Client, facture, achat ou opération n’est encore créée ou modifiée par ce lot.',
      'La lecture XLSX sera ajoutée séparément après validation de ses contraintes de sécurité et de déploiement.',
    ],
  },
  {
    version: 'D00.01.10#99',
    date: '19 septembre 2026',
    title: 'Rétablissement technique de DEV',
    changes: [
      'La préparation technique des imports Freebe a été retirée temporairement après un échec de redémarrage de l’environnement DEV.',
      'Aucune donnée Freebe n’avait encore été importée ou modifiée par ce premier lot.',
      'Le développement reprendra après un diagnostic isolé, sans remettre en cause le socle stable déjà publié.',
    ],
  },
  {
    version: 'D00.01.10#96',
    date: '19 septembre 2026',
    title: 'Fiabilité des accès renforcée',
    changes: [
      'Les comptes liés à une organisation suspendue sont désormais refusés de manière uniforme dans les différents parcours de connexion.',
      'Les opérations de suspension, d’archivage et de restauration restent fiables même lorsque d’anciennes demandes de sécurité ont expiré.',
      'Les notifications de sécurité disposent désormais de la sortie réseau nécessaire à leur acheminement.',
    ],
  },
  {
    version: 'D00.01.10#89',
    date: '18 septembre 2026',
    title: 'Configuration des accès renforcée',
    changes: [
      'Tosho vérifie désormais la cohérence entre son adresse publique et la configuration utilisée par les passkeys.',
      'La préparation des environnements est plus stricte afin d’éviter qu’une mauvaise configuration réseau ne dégrade les protections de connexion.',
      'Le futur environnement de production reste configurable sans imposer de nom de domaine tant que l’adresse canonique n’a pas été validée.',
    ],
  },
  {
    version: 'D00.01.10#87',
    date: '18 septembre 2026',
    title: 'Vérification anti-abus adaptative préparée',
    changes: [
      'Tosho est prêt à demander une vérification reCAPTCHA uniquement après plusieurs tentatives suspectes de connexion ou de récupération de mot de passe.',
      'Le service reCAPTCHA n’est pas chargé pendant un usage normal afin de limiter les sollicitations externes inutiles.',
      'La protection reste désactivée tant que les clés dédiées à l’environnement DEV ne sont pas provisionnées.',
    ],
  },
  {
    version: 'D00.01.10#86',
    date: '18 septembre 2026',
    title: 'Protection anti-abus étendue aux accès sensibles',
    changes: [
      'La récupération de mot de passe, l’activation de compte et la connexion par passkey sont désormais mieux protégées contre les tentatives répétées.',
      'Tosho peut demander de patienter temporairement lorsqu’un trop grand nombre d’essais est détecté.',
      'Les messages restent neutres afin de ne pas révéler si une adresse ou un compte existe.',
    ],
  },
  {
    version: 'D00.01.10#84',
    date: '17 septembre 2026',
    title: 'Protection renforcée de la connexion',
    changes: [
      'Les tentatives répétées de connexion sont désormais temporairement limitées afin de réduire les abus automatisés.',
      'Après plusieurs codes de vérification incorrects, le parcours de connexion doit être recommencé afin de protéger le compte.',
      'Les connexions par mot de passe, code de vérification et passkey restent disponibles comme auparavant.',
    ],
  },
  {
    version: 'D00.01.09#81',
    date: '17 septembre 2026',
    title: 'Restauration des comptes supprimés',
    changes: [
      'Lorsqu’une adresse e-mail correspond à un compte supprimé, Tosho propose désormais de restaurer ce compte au lieu de créer un doublon.',
      'La restauration conserve la même identité et son historique de rattachement.',
      'Les anciens moyens d’accès sont neutralisés et une nouvelle invitation d’activation est envoyée avant le retour du compte.',
    ],
  },
  {
    version: 'D00.01.09#80',
    date: '17 septembre 2026',
    title: 'Correctifs de recette Mon compte',
    changes: [
      'Le menu de profil se referme désormais au clic extérieur, avec Échap ou après cinq secondes d’inactivité.',
      'Le changement d’adresse e-mail précise que le même code TOTP à 6 chiffres reste utilisé après reconnexion.',
      'Le pictogramme Passkeys conserve une taille cohérente sur les petits écrans.',
      'Le Superviseur peut supprimer logiquement une identité sans rattachement courant, avec confirmation et traçabilité.',
    ],
  },
  {
    version: 'D00.01.09#77',
    date: '15 septembre 2026',
    title: 'Mon compte et sessions personnelles',
    changes: [
      'Un nouvel espace Mon compte réunit les principales informations personnelles et les réglages de sécurité du profil.',
      'L’adresse e-mail, le mot de passe et les passkeys peuvent désormais être gérés depuis le même espace.',
      'Les sessions actives du compte peuvent être consultées puis déconnectées individuellement ou toutes ensemble.',
      'Un journal personnel permet de retrouver les principales actions de sécurité récentes du compte.',
    ],
  },
  {
    version: 'D00.01.08#67',
    date: '14 septembre 2026',
    title: 'Interventions sensibles plus claires',
    changes: [
      'Tosho vérifie désormais la validité de la session avant d’ouvrir une confirmation de désactivation, réactivation ou suppression logique.',
      'Si une reconnexion est nécessaire, le message est affiché immédiatement dans l’écran Utilisateurs au lieu d’être masqué derrière une pop-in.',
      'La vérification de sécurité reste également appliquée au moment de confirmer l’intervention.',
    ],
  },
  {
    version: 'D00.01.08#66',
    date: '14 septembre 2026',
    title: 'Confirmation de détachement mieux structurée',
    changes: [
      'Le motif de détachement est désormais présenté sur une ligne dédiée avec des marges cohérentes dans la pop-in.',
      'La zone de saisie occupe proprement la largeur utile de la fenêtre et le compteur de caractères reste aligné sous le champ.',
      'La présentation reste adaptée aux écrans desktop et smartphone sans modifier le fonctionnement du détachement.',
    ],
  },
  {
    version: 'D00.01.08#65',
    date: '12 septembre 2026',
    title: 'Alignements mobiles harmonisés',
    changes: [
      'Les contenus de l’espace Organisations utilisent désormais des gouttières cohérentes sur smartphone.',
      'Les blocs, cartes et lignes suivent les mêmes axes horizontaux pour éviter les différences de largeur visuelles.',
      'Le bandeau supérieur reste en place sur mobile afin que le contenu ne remonte pas sous l’interface dynamique de Safari.',
    ],
  },
  {
    version: 'D00.01.08#64',
    date: '12 septembre 2026',
    title: 'Safari mobile plus stable dans les formulaires',
    changes: [
      'La loupe du champ de recherche pour rattacher un Administrateur reste désormais séparée du texte et du placeholder.',
      'Les champs de saisie sur smartphone conservent une taille lisible qui évite le zoom automatique gênant de Safari iOS.',
      'Le zoom manuel du navigateur reste disponible : Tosho ne désactive pas les fonctions d’accessibilité du navigateur.',
    ],
  },
  {
    version: 'D00.01.08#62',
    date: '12 septembre 2026',
    title: 'Gestion mobile des Administrateurs améliorée',
    changes: [
      'Les Administrateurs sont présentés sous forme de fiches lisibles sur mobile, sans défilement horizontal.',
      'Les informations de compte, d’invitation et les actions restent visibles ensemble dans la largeur du navigateur.',
      'La confirmation de détachement s’ouvre désormais comme une véritable pop-in centrée au-dessus de l’interface.',
      'Les actions et accents rouges de supervision utilisent le rouge Tosho officiel.',
    ],
  },
  {
    version: 'D00.01.08#61',
    date: '12 septembre 2026',
    title: 'Affichage mobile des organisations corrigé',
    changes: [
      'L’espace Organisations reste contenu dans la largeur du navigateur sur mobile.',
      'Les titres, aides, compteurs et informations d’organisation se réorganisent sans déborder horizontalement.',
      'Les métadonnées longues reviennent à la ligne au lieu d’être coupées hors de l’écran.',
    ],
  },
  {
    version: 'D00.01.08#59',
    date: '12 septembre 2026',
    title: 'Gestion des rattachements Administrateur fiabilisée',
    changes: [
      'Les recherches Organisation et Administrateur restent lisibles même avec leur pictogramme de recherche.',
      'Le Superviseur peut détacher proprement un Administrateur d’une organisation sans supprimer son compte ni son historique.',
      'Une organisation peut temporairement rester sans Administrateur actif lorsqu’une intervention de supervision l’exige.',
      'Un compte détaché peut être rattaché de nouveau ultérieurement lorsque son état global le permet.',
    ],
  },
  {
    version: 'D00.01.08#55',
    date: '12 septembre 2026',
    title: 'Création et rattachement des organisations',
    changes: [
      'Le Superviseur dispose désormais d’un espace Organisations avec recherche progressive.',
      'Une nouvelle organisation peut être créée avec ses informations principales puis recevoir son premier Administrateur.',
      'Un compte existant éligible peut être retrouvé et rattaché comme Administrateur, tandis qu’un compte déjà rattaché reste protégé contre un second rattachement.',
      'La déconnexion et les autres actions sensibles renouvellent leur preuve de sécurité au moment de l’action pour éviter les échecs liés à une preuve devenue ancienne.',
    ],
  },
  {
    version: 'D00.01.08#54',
    date: '11 septembre 2026',
    title: 'Interventions de support sur les comptes',
    changes: [
      'Le Superviseur peut désormais désactiver, réactiver ou supprimer logiquement un compte depuis l’annuaire global.',
      'Les actions sensibles demandent une confirmation claire et un motif lorsque l’accès est désactivé ou supprimé.',
      'Les sessions concernées sont immédiatement révoquées afin que la modification d’accès prenne effet sans délai.',
      'Tosho protège automatiquement le dernier Administrateur actif d’une organisation contre une désactivation ou suppression accidentelle.',
    ],
  },
  {
    version: 'D00.01.08#52',
    date: '11 septembre 2026',
    title: 'Tableau de bord du Superviseur',
    changes: [
      'Ajout d’un véritable écran d’accueil pour suivre les principaux indicateurs de la plateforme.',
      'Nouvel annuaire global des Administrateurs, Prestataires et Clients avec leur organisation et leur état d’accès.',
      'Ajout de filtres pour retrouver plus rapidement un compte lors d’une demande de support.',
      'Les indicateurs Projets et Serveurs restent explicitement en attente tant que leurs sources ne sont pas raccordées.',
    ],
  },
  {
    version: 'D00.01.08#50',
    date: '11 septembre 2026',
    title: 'Supervision des administrateurs',
    changes: [
      'Nouvel espace Superviseur pour consulter les administrateurs d’organisation sans accéder à leurs données métier.',
      'Possibilité de renvoyer une invitation lorsqu’un compte administrateur est encore en attente d’activation.',
      'Ajout d’une action de sécurité permettant de déconnecter tous les utilisateurs après confirmation explicite.',
      'Nouvelle convention de versionnement Tosho avec préfixes D et P en majuscules.',
    ],
  },
  {
    version: 'd00.01.07#46',
    date: '11 septembre 2026',
    title: 'Passkeys finalisées',
    changes: [
      'Parcours réel validé : ajout, connexion, retrait et impossibilité de réutiliser une passkey retirée.',
      'Confirmation de retrait rendue plus visible pour éviter toute ambiguïté.',
      'Connexion classique par mot de passe et Microsoft Authenticator toujours disponible.',
    ],
  },
  {
    version: 'd00.01.07#45',
    date: '10 septembre 2026',
    title: 'Connexion par passkey',
    changes: [
      'Connexion simplifiée avec une passkey sur les appareils et clés de sécurité compatibles.',
      'Gestion des passkeys depuis le profil : ajout, nommage et retrait.',
      'Connexion classique par mot de passe et Microsoft Authenticator conservée.',
    ],
  },
  {
    version: 'd00.01.06#37',
    date: '10 septembre 2026',
    title: 'Finitions visuelles des fenêtres',
    changes: [
      'Croix de fermeture déplacées hors des fenêtres pour mieux se fondre dans l’overlay.',
      'Poignée du menu latéral simplifiée pour se confondre avec la barre de navigation.',
    ],
  },
  {
    version: 'd00.01.06#36',
    date: '10 septembre 2026',
    title: 'Responsive et navigation finalisés',
    changes: [
      'Suppression des débordements horizontaux sur les écrans de largeur intermédiaire.',
      'Recherche compacte en pop-in lorsque la place manque dans la barre supérieure.',
      'Comportements de survol harmonisés et navigation compacte plus lisible.',
      'Ajout du changelog directement accessible depuis l’interface connectée.',
    ],
  },
  {
    version: 'd00.01.06#35',
    date: '10 septembre 2026',
    title: 'Navigation responsive améliorée',
    changes: [
      'Menu latéral repliable pour laisser davantage de place au contenu.',
      'Navigation mobile simplifiée avec les pictogrammes validés.',
      'Icônes et actions principales harmonisées entre les différents profils.',
    ],
  },
  {
    version: 'd00.01.06#34',
    date: '10 septembre 2026',
    title: 'Interface rapprochée du prototype validé',
    changes: [
      'Palette plus chaleureuse et hiérarchie visuelle harmonisée.',
      'Barre supérieure, notifications, profil et menus retravaillés.',
      'Tableau de bord enrichi visuellement sans ajouter de données fictives.',
      'Favicon Tosho harmonisé sur tous les thèmes du navigateur.',
    ],
  },
  {
    version: 'd00.01.06#33',
    date: '9 septembre 2026',
    title: 'Branding et lisibilité améliorés',
    changes: [
      'Logo Tosho et identité des organisations mieux intégrés dans les espaces connectés.',
      'Lisibilité générale améliorée sur les tableaux de bord.',
      'Messages de sécurité simplifiés pour être plus compréhensibles.',
    ],
  },
  {
    version: 'd00.01.06#32',
    date: '9 septembre 2026',
    title: 'Parcours d’accès validés',
    changes: [
      'Validation réelle des accès Administrateur, Prestataire, Client et Superviseur.',
      'Activation de compte, mot de passe et authentification renforcée réunis dans un même parcours.',
      'Amélioration visuelle de l’écran de connexion.',
    ],
  },
  {
    version: 'd00.01.06#31',
    date: '9 septembre 2026',
    title: 'Connexion plus claire et plus fiable',
    changes: [
      'Messages de protection des données rendus plus simples et plus accessibles.',
      'Fiabilité du processus de publication renforcée.',
    ],
  },
  {
    version: 'd00.01.06#30',
    date: '9 septembre 2026',
    title: 'Univers graphique de la forge finalisé',
    changes: [
      'Image de forge validée et intégrée dans sa version définitive.',
      'Contrôles renforcés pour préserver l’intégrité des éléments graphiques importants.',
    ],
  },
  {
    version: 'd00.01.06#29',
    date: '9 septembre 2026',
    title: 'Chargement des visuels fiabilisé',
    changes: [
      'Gestion des ressources graphiques rendue plus robuste entre construction et publication.',
    ],
  },
  {
    version: 'd00.01.06#28',
    date: '9 septembre 2026',
    title: 'Versionnement visible et traçable',
    changes: [
      'Ajout du numéro de version visible pour faciliter le support et les vérifications.',
      'Stabilisation de l’identité visuelle de la page de connexion.',
    ],
  },
  {
    version: 'v0 — Accès sécurisés',
    date: '8 septembre 2026',
    title: 'Connexion et gestion du compte',
    changes: [
      'Intégration de la connexion sécurisée et de la déconnexion.',
      'Ajout de la récupération de mot de passe et de la gestion sécurisée du changement d’adresse e-mail.',
      'Gestion des sessions actives et renforcement des contrôles d’accès.',
    ],
  },
  {
    version: 'v0 — MFA et invitations',
    date: '3 septembre 2026',
    title: 'Authentification renforcée',
    changes: [
      'Intégration de l’authentification MFA compatible avec Microsoft Authenticator.',
      'Ajout des invitations de compte et des codes de récupération.',
    ],
  },
  {
    version: 'v0 — Protection des données',
    date: '3 septembre 2026',
    title: 'Données sensibles mieux protégées',
    changes: [
      'Renforcement du chiffrement des données et secrets sensibles.',
      'Protection accrue des mots de passe, jetons de sécurité et sessions utilisateur.',
    ],
  },
  {
    version: 'v0 — Droits et cloisonnement',
    date: '3 septembre 2026',
    title: 'Accès par rôle',
    changes: [
      'Mise en place des droits Administrateur, Prestataire, Client et Superviseur.',
      'Renforcement du cloisonnement entre les organisations et leurs données.',
    ],
  },
  {
    version: 'v0 — Comptes et organisations',
    date: '3 septembre 2026',
    title: 'Fondations des profils Tosho',
    changes: [
      'Création du modèle de comptes, organisations et rattachements utilisateurs.',
      'Préparation des cycles de vie des comptes et des organisations.',
    ],
  },
  {
    version: 'v0 — Fondation',
    date: '28 août au 1er septembre 2026',
    title: 'Première base de Tosho',
    changes: [
      'Mise en place du socle applicatif et de l’environnement de développement.',
      'Premiers mécanismes de sauvegarde, restauration et publication contrôlée.',
      'Préparation de l’architecture nécessaire aux futurs modules métier.',
    ],
  },
] as const;
